import React, { useEffect, useRef, useContext, createContext, useState, useCallback, memo, useImperativeHandle } from 'react';
import { basicSetup } from 'codemirror';
import { EditorState, StateEffect, StateField, RangeSetBuilder, Transaction } from '@codemirror/state';
import { EditorView, keymap, Decoration, WidgetType, MatchDecorator, ViewPlugin, ViewUpdate } from '@codemirror/view';
import { LaTeXLanguage } from './languages/latex/latex-language.ts'
import { LanguageSupport, } from '@codemirror/language'
import { linter, lintGutter } from '@codemirror/lint';
import { indentWithTab, invertedEffects } from "@codemirror/commands"
import { openAutocomplete } from './languages/latex/open-autocomplete.ts'
import { latexIndentService } from './languages/latex/latex-indent-service.ts'
import { shortcuts } from './languages/latex/shortcuts.ts';
import { html } from '@codemirror/lang-html';
import axios from 'axios';
import {
  argumentCompletionSources,
  explicitCommandCompletionSource,
  inCommandCompletionSource,
  beginEnvironmentCompletionSource,
} from './languages/latex/complete.ts'
import { isVisual } from './extensions/visual/visual.ts'
import { metadata } from './languages/latex/metadata.ts'
import { documentCommands } from './languages/latex/document-commands.ts'
import { documentEnvironmentNames } from './languages/latex/document-environment-names.ts'
import LintWorker from './languages/latex/linter/latex-linter.worker.js'
import { ToolbarItems } from './toolbar-item.js';
import { FaRegFilePdf, FaRegComment, FaEllipsisH, FaTag, } from "react-icons/fa";
import { Button } from 'react-bootstrap';
import { styleEditing, referanceOrdering, clearCacheFiles, CleanupEditor, iRefEditor, XMPCreation, issueUpdation, crossRef } from './button-function.js';
import { toastError, toastSuccess, toastWarning } from '../toaster';
import moment from 'moment';
import pathconfig from '../../pathconfig.json';
import packjson from '../../../package.json';
import { useAppContextProvider } from '../../contextapi';
import spellCheckExtension, { spellCheckTheme } from './extension/spellChecker.js';
import { CommentUpdateFunction } from './button-function.js';
import { mathClickablePlugin } from './extensions/math-preview.ts';
// import InlpActionModal from './InlpActionModal.js';
// import ReactDOM from 'react-dom';

const lintWorker = new LintWorker();
let userDetails;
const completionSources = [
  ...argumentCompletionSources,
  inCommandCompletionSource,
  explicitCommandCompletionSource,
  beginEnvironmentCompletionSource
]
let globalView = null;

let altTextClickCallback = null;

export function setAltTextClickHandler(fn) {
  altTextClickCallback = typeof fn === 'function' ? fn : null;
}

const autocompleteTheme = EditorView.baseTheme({
  '.cm-tooltip.cm-tooltip-autocomplete': {
    // shift the tooltip, so the completion aligns with the text
    marginLeft: '-4px',
  },
  '&light .cm-tooltip.cm-tooltip-autocomplete, &light .cm-tooltip.cm-completionInfo':
  {
    border: '1px lightgray solid',
    background: '#fefefe',
    color: '#111',
    boxShadow: '2px 3px 5px rgb(0 0 0 / 20%)',
  },
  '&dark .cm-tooltip.cm-tooltip-autocomplete, &dark .cm-tooltip.cm-completionInfo':
  {
    border: '1px #484747 solid',
    boxShadow: '2px 3px 5px rgba(0, 0, 0, 0.51)',
    background: '#25282c',
    color: '#c1c1c1',
  },

  // match editor font family and font size, so the completion aligns with the text
  '.cm-tooltip.cm-tooltip-autocomplete > ul': {
    fontFamily: 'var(--source-font-family)',
    fontSize: 'var(--font-size)',
  },
  '.cm-tooltip.cm-tooltip-autocomplete li[role="option"]': {
    display: 'flex',
    justifyContent: 'space-between',
    lineHeight: 1.4, // increase the line height from default 1.2, for a larger target area
    outline: '1px solid transparent',
  },
  '&light .cm-tooltip.cm-tooltip-autocomplete li[role="option"]:hover': {
    outlineColor: '#abbffe',
    backgroundColor: 'rgba(233, 233, 253, 0.4)',
  },
  '&dark .cm-tooltip.cm-tooltip-autocomplete li[role="option"]:hover': {
    outlineColor: 'rgba(109, 150, 13, 0.8)',
    backgroundColor: 'rgba(58, 103, 78, 0.62)',
  },
  '.cm-tooltip.cm-tooltip-autocomplete ul li[aria-selected]': {
    color: 'inherit',
  },
  '&light .cm-tooltip.cm-tooltip-autocomplete ul li[aria-selected]': {
    background: '#cad6fa',
  },
  '&dark .cm-tooltip.cm-tooltip-autocomplete ul li[aria-selected]': {
    background: '#3a674e',
  },
  '.cm-completionMatchedText': {
    textDecoration: 'none', // remove default underline,
  },
  '&light .cm-completionMatchedText': {
    color: '#2d69c7',
  },
  '&dark .cm-completionMatchedText': {
    color: '#93ca12',
  },
  '.ol-cm-completionType': {
    paddingLeft: '1em',
    paddingRight: 0,
    width: 'auto',
    fontSize: '90%',
    fontFamily: 'var(--source-font-family)',
    opacity: '0.5',
  },
  '.cm-completionInfo .ol-cm-symbolCompletionInfo': {
    margin: 0,
    whiteSpace: 'normal',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
  },
  '.cm-completionInfo .ol-cm-symbolCharacter': {
    fontSize: '32px',
  },
})
const trackChangesTheme = EditorView.baseTheme({
  '.cm-line': {
    overflowX: 'hidden', // needed so the callout elements don't overflow (requires line wrapping to be on)
  },
  '&light .ol-cm-change-i': {
    backgroundColor: '#2c8e304d',
  },
  '&dark .ol-cm-change-i': {
    backgroundColor: 'rgba(37, 107, 41, 0.15)',
  },
  '&light .ol-cm-change-c': {
    backgroundColor: '#f3b1114d',
  },
  '&dark .ol-cm-change-c': {
    backgroundColor: 'rgba(194, 93, 11, 0.15)',
  },
  '.ol-cm-change': {
    padding: 'var(--half-leading, 0) 0',
  },
  '.ol-cm-change-d': {
    borderLeft: '2px dotted #c5060b',
    marginLeft: '-1px',
  },
  '.ol-cm-change-callout': {
    position: 'relative',
    pointerEvents: 'none',
    padding: 'var(--half-leading, 0) 0',
  },
  '.ol-cm-change-callout-inner': {
    display: 'inline-block',
    position: 'absolute',
    left: 0,
    bottom: 0,
    width: '100vw',
    borderBottom: '1px dashed black',
  },
  // disable callout line in Firefox
  '@supports (-moz-appearance:none)': {
    '.ol-cm-change-callout-inner': {
      display: 'none',
    },
  },
  '.ol-cm-change-callout-i .ol-cm-change-callout-inner': {
    borderColor: '#2c8e30',
  },
  '.ol-cm-change-callout-c .ol-cm-change-callout-inner': {
    borderColor: '#f3b111',
  },
  '.ol-cm-change-callout-d .ol-cm-change-callout-inner': {
    borderColor: '#c5060b',
  }
})
const inlpDecoration = Decoration.mark({
  attributes: {
    style: 'text-decoration: underline; text-decoration-style: solid; text-decoration-thickness: 3px; text-decoration-color: #ffd700;'
  }
});
const changeMark = (text, meta) => Decoration.mark({
  tagName: 'span',
  class: `ol-cm-change ol-cm-change-i ol-cm-change-user-${meta.role.replace(/\s+/g, '-').toLowerCase()}`,
  attributes: {
    'data-text': text,
    'data-meta': JSON.stringify(meta),
    'data-from': meta.from,
    'data-to': meta.to,
    'data-user': meta.Username,
    'data-role': meta.role,
    ...(meta.altTextKey ? { 'data-alttext-key': meta.altTextKey } : {})
  }
});

const commentMark = (comment, cid, commentmeta) => Decoration.mark({
  tagName: 'span',
  class: 'ol-cm-change ol-cm-change-c',
  attributes: {
    'data-comment': comment,
    'data-id': cid,
    'data-meta': JSON.stringify(commentmeta),
    'data-from': commentmeta.from,
    'data-to': commentmeta.to,
    'data-user': commentmeta.Username,
    'data-role': commentmeta.role || ''
  },
});

class ChangeDeletedWidget extends WidgetType {
  constructor(text) {
    super()
    this.text = text;
  }

  toDOM() {
    const widget = document.createElement('span')
    widget.classList.add('ol-cm-change')
    widget.classList.add('ol-cm-change-d')
    // widget.attributes.add({ 'data-deleted-text': this.text });

    return widget
  }

  eq() {
    return true
  }
}

const isValidDOMRect = (rect) => {
  return rect && (rect.width > 0 || rect.height > 0 || rect.top > 0 || rect.left > 0);
};

class InlpTooltipWidget extends WidgetType {
  constructor({ highlightText, replaceText, from, to, view, fullLine, changes, inlpActions, title, group, action, isActive = false }) {
    super();
    this.highlightText = highlightText || ' ';
    this.replaceText = replaceText;
    this.from = from;
    this.to = to;
    this.view = view;
    this.fullLine = fullLine;
    this.changes = changes;
    this.inlpActions = inlpActions;
    this.title = title || "Suggestion";
    this.group = group || "grammar";
    this.action = action || "replace";
    this.id = `inlp-${from}-${to}-${Date.now()}`;
    this.isReadOnly = view?.state?.facet(EditorState.readOnly) || false;
    this.isActive = isActive; // New property to track active state
  }
  // Add this method to the InlpTooltipWidget class
  navigateToActionWithValidRect(nextAction, nextWidget) {
    if (!globalView || !nextAction || !nextWidget) return;

    try {
      // Remove active class from all widgets
      const allWidgets = document.querySelectorAll('.inlp-action-widget.inlp-active-suggestion');
      allWidgets.forEach(widget => {
        widget.classList.remove('inlp-active-suggestion');
        widget.style.backgroundColor = '#ffd700';
        widget.style.color = '#000';
        widget.style.fontWeight = 'normal';
        widget.style.boxShadow = 'none';
      });

      // Add active class to the next widget
      nextWidget.classList.add('inlp-active-suggestion');
      nextWidget.style.backgroundColor = 'orange';
      nextWidget.style.fontWeight = 'bold';
      nextWidget.style.boxShadow = '0 0 5px rgba(255, 69, 0, 0.7)';

      // Scroll to the position
      globalView.dispatch({
        effects: EditorView.scrollIntoView(nextAction.from, { y: 'center' })
      });

      // Select the text range
      globalView.dispatch({
        selection: { anchor: nextAction.from, head: nextAction.to }
      });

      // Wait a bit for the DOM to update, then trigger the tooltip
      setTimeout(() => {
        const updatedRect = nextWidget.getBoundingClientRect();

        if (isValidDOMRect(updatedRect)) {
          // Create a new widget instance for the next action
          const nextWidgetInstance = new InlpTooltipWidget({
            highlightText: nextAction.highlightText,
            replaceText: nextAction.replaceText,
            from: nextAction.from,
            to: nextAction.to,
            view: globalView,
            fullLine: nextAction.fullLine,
            changes: this.inlpActions.filter(change => change.fullLine === nextAction.fullLine),
            inlpActions: this.inlpActions,
            title: nextAction.title,
            group: nextAction.group,
            action: nextAction.action,
            isActive: true
          });

          // Call handleClick on the new widget instance
          nextWidgetInstance.handleClick({ stopPropagation: () => { } }, nextWidget);
        } else {
          console.log('DOMRect still invalid after navigation, trying next action');
          // Recursively try the next action
          // nextWidgetInstance.skipToNextValidTooltip();
        }
      }, 100);

    } catch (error) {
      console.error("Error during navigation to action with valid rect:", error);
    }
  }

  toDOM() {
    const span = document.createElement('span');
    span.className = 'inlp-action-widget';

    // Add active class if this suggestion is active
    if (this.isActive) {
      span.classList.add('inlp-active-suggestion');
    }

    // Check if this is an insert action that was undone
    const isUndoneInsert = this.isUndoRedo &&
      (this.highlightText === '' || this.highlightText === ' ') &&
      this.replaceText &&
      this.replaceText.trim() !== '';

    if (isUndoneInsert) {
      // Special handling for end-of-document insertions
      if (this.isEndOfDoc) {
        span.textContent = '\u200B'; // Zero-width space
        span.style.borderLeft = '2px solid #ffd700';
        span.style.display = 'inline-block';
        span.style.width = '2px';
        span.style.height = '1em';
        span.style.verticalAlign = 'text-bottom';
      }
      // Normal insert action in middle of text
      else {
        span.textContent = '\u200B'; // Zero-width space
        span.style.textDecoration = 'underline solid #ffd700 3px';
        span.style.backgroundColor = 'rgba(255, 215, 0, 0.3)';
        span.style.color = 'transparent';
        span.style.display = 'inline-block';
        span.style.width = '2px';
        span.style.verticalAlign = 'text-bottom';
      }
    } else if (!this.isUndoRedo) {
      // Normal display for non-undo operations
      span.textContent = this.highlightText;

      // Apply different styling for active suggestion
      if (this.isActive) {
        // span.style.textDecoration = 'underline solid #000 3px'; // Orange-red underline
        span.style.backgroundColor = 'orange'; // Orange-red background
        // span.style.color = 'white';
        span.style.fontWeight = 'bold';
        span.style.boxShadow = '0 0 5px rgba(255, 69, 0, 0.7)'; // Subtle glow effect
      } else {
        // span.style.textDecoration = 'underline solid #ffd700 3px';
        span.style.backgroundColor = '#ffd700';
        span.style.color = '#000';
      }
    }

    span.dataset.inlpId = this.id;
    span.style.display = 'inline-block';
    span.title = this.title || "Suggestion";

    span.onclick = (e) => this.handleClick(e, span);
    if (!this.isReadOnly) {
    } else {
      // span.style.cursor = 'not-allowed';
      // span.title = 'Editor is in read-only mode';
      span.style.opacity = '0.7';
    }

    return span;
  }

  // Add this method to the InlpTooltipWidget class
  skipToNextValidTooltip() {
    if (!this.inlpActions || !Array.isArray(this.inlpActions)) {
      console.log('No inlpActions available for skipping');
      return;
    }

    // Find current action index
    const currentIndex = this.inlpActions.findIndex(action =>
      action.from === this.from &&
      action.to === this.to &&
      action.replaceText === this.replaceText
    );

    // Look for the next action with a valid DOMRect
    for (let i = 1; i < this.inlpActions.length; i++) {
      const nextIndex = (currentIndex + i) % this.inlpActions.length;
      const nextAction = this.inlpActions[nextIndex];

      if (!nextAction) continue;

      // Try to find the widget for this action
      const nextWidget = document.querySelector(`[data-inlp-id^="inlp-${nextAction.from}-${nextAction.to}"]`);

      if (nextWidget) {
        const nextRect = nextWidget.getBoundingClientRect();
        console.log(`Checking next action at index ${nextIndex}, rect:`, nextRect);

        if (isValidDOMRect(nextRect)) {
          console.log('Found valid DOMRect, showing tooltip for next action');
          // Navigate to this action and show its tooltip
          this.navigateToActionWithValidRect(nextAction, nextWidget);
          return;
        }
      }
    }

    console.log('No actions with valid DOMRect found');
  }


  // In the InlpTooltipWidget class, modify the findDuplicateSuggestions method
  findDuplicateSuggestions() {
    if (!this.inlpActions || !this.highlightText || !this.replaceText) return [];

    // Use the global inlpActions from window.appContext if available
    const actionsToSearch = window.appContext?.inlpActions || this.inlpActions;

    return actionsToSearch.filter(action =>
      action.highlightText === this.highlightText &&
      action.replaceText === this.replaceText &&
      !(action.from === this.from && action.to === this.to) // Exclude the current action
    );
  }

  handleClick(event, span) {
    event.stopPropagation();
    const isReadOnly = !!this.view?.state?.facet(EditorState.readOnly);
    const disabledAttr = ''; // keep empty
    const disabledClass = isReadOnly ? 'disabled' : '';

    // if (this.view?.state?.facet(EditorState.readOnly)) {
    //   return; 
    // }

    // --- Always sync position before using ---
    let syncedAction = null;
    if (globalView && typeof syncInlpPositions === 'function') {
      const synced = syncInlpPositions([
        {
          from: this.from,
          to: this.to,
          highlightText: this.highlightText,
          replaceText: this.replaceText,
          fullLine: this.fullLine,
          id: this.id
        }
      ], globalView.state.doc);
      if (synced && synced.length > 0) {
        syncedAction = synced[0];
      }
    }
    const from = syncedAction ? syncedAction.from : this.from;
    const to = syncedAction ? syncedAction.to : this.to;
    const highlightText = syncedAction ? syncedAction.highlightText : this.highlightText;
    const replaceText = syncedAction ? syncedAction.replaceText : this.replaceText;

    console.log("Manual click action data (synced):", {
      from,
      to,
      highlightText,
      replaceText,
      isEmpty: highlightText === '' || highlightText === ' '
    });

    const existingTooltip = document.querySelector('.inlp-tooltip');
    if (existingTooltip) existingTooltip.remove();

    const rect = span.getBoundingClientRect();
    console.log('<<<rect>>>', rect);

    // **NEW LOGIC: Check if DOMRect is invalid**
    if (!isValidDOMRect(rect)) {
      console.log('Invalid DOMRect detected, skipping to next tooltip');
      this.skipToNextValidTooltip();
      return;
    }

    const tooltip = document.createElement('div');
    tooltip.className = 'inlp-tooltip';

    tooltip.style.position = 'fixed';
    tooltip.style.left = `${rect.left}px`;
    tooltip.style.top = `${rect.bottom + 5}px`;

    // modified for the delete action as it is taking empty as a delete action 
    // const isDeleteAction = !this.replaceText || this.replaceText.trim() === '';
    const isDeleteAction = this.replaceText === '';
    // Check for duplicate suggestions
    const duplicates = this.findDuplicateSuggestions();
    const hasDuplicates = duplicates.length > 0 || this.hasDuplicates === true;

    // Create appropriate tooltip content based on action type
    let tooltipContent;

    if (isDeleteAction) {
      // For delete actions, show a special delete UI
      tooltipContent = `
        <div class="delete-action-ui">
          <span class="delete-icon">🗑️</span>
          <span class="delete-text">${highlightText}</span>
        </div>
      `;
    } else if (highlightText === '' || highlightText === ' ') {
      // For insert actions (empty highlightText)
      tooltipContent = `
        <span class="strike-text empty-text">
          Empty text
        </span> → 
        <span class="replace-text">${replaceText}</span>
      `;
    } else {
      // For replace actions
      tooltipContent = `
        <span class="highlight-text">${highlightText}</span> → 
        <span class="replace-text">${replaceText}</span>
      `;
    }

    // Add duplicate count if there are duplicates
    const duplicateInfo = hasDuplicates ?
      `<div class="duplicate-info">${duplicates.length + 1} occurrences found</div>` : '';

    tooltip.innerHTML = `
  <div class="tooltip-content">
    <div class="${this.highlightText === '' || this.highlightText === ' ' ? 'sentence-context-empty' : 'sentence-context'}">
      ${tooltipContent}
    </div>
    ${duplicateInfo}
    ${!isReadOnly
        ? `<div class="tooltip-actions">
            <button class="accept ${disabledClass}" aria-disabled="false">${isDeleteAction ? 'Delete' : 'Accept'}</button>
            <button class="reject ${disabledClass}" aria-disabled="false">Ignore</button>
            ${hasDuplicates ? `<button class="ignore-all ${disabledClass}" aria-disabled="false">Ignore All</button>` : ''}
          </div>`
        : ''
      }
  </div>
`;

    document.body.appendChild(tooltip);
    this.setupTooltipActions(tooltip, hasDuplicates, duplicates);
    if (isReadOnly) {
      // Disable all buttons
      tooltip.querySelectorAll('button').forEach(btn => {
        btn.disabled = true;
        btn.classList.add('disabled');
        btn.onclick = (e) => {
          e.preventDefault();
          e.stopPropagation();
          // Optionally, just close the tooltip on click
          tooltip.remove();
        };
      });
    }
  }
  addTooltipCloseOnClickOutside(tooltip) {
    // Remove any previous listeners to avoid duplicates
    document.removeEventListener('mousedown', window._inlpTooltipCloseHandler);

    window._inlpTooltipCloseHandler = (e) => {
      if (!tooltip.contains(e.target)) {
        tooltip.remove();
        document.removeEventListener('mousedown', window._inlpTooltipCloseHandler);
      }
    };
    setTimeout(() => {
      document.addEventListener('mousedown', window._inlpTooltipCloseHandler);
    }, 0);
  }
  setupTooltipActions(tooltip, hasDuplicates, duplicates) {
    const isReadOnly = this.view?.state?.facet(EditorState.readOnly);
    if (isReadOnly) {
      // mark tooltip as read-only (useful for CSS)
      tooltip.classList.add('read-only');

      tooltip.querySelectorAll('button').forEach(btn => {
        // visually disable
        btn.classList.add('disabled');
        btn.setAttribute('aria-disabled', 'true');
        // apply inline styles so it's always visible even without stylesheet
        btn.style.opacity = '0.6';
        btn.style.cursor = 'not-allowed';

        // Do not perform the normal action — only close the tooltip
        btn.onclick = (e) => {
          e.preventDefault();
          e.stopPropagation();
          // Optionally show a toast or message saying "Editor is read-only"
          // showToast && showToast("Editor is read-only");
          tooltip.remove();
        };
      });
      this.addTooltipCloseOnClickOutside(tooltip);
      return;
    }
    this.addTooltipCloseOnClickOutside(tooltip);

    // if (this.view?.state?.facet(EditorState.readOnly)) {
    //   tooltip.remove();
    //   return;
    // }


    tooltip.querySelector('.accept').onclick = () => {
      console.log("Accept button clicked with data:", {
        from: this.from,
        to: this.to,
        highlightText: this.highlightText,
        replaceText: this.replaceText,
        isEmpty: this.highlightText === '' || this.highlightText === ' '
      });
      if (!globalView) return;
      if (globalView?.state?.facet(EditorState.readOnly)) {
        tooltip.remove();
        return;
      }

      // Create a unique identifier for this action
      const actionIdentifier = {
        from: this.from,
        to: this.to,
        replaceText: this.replaceText,
        id: this.id
      };

      // Find the current action in the inlpActions array
      // Add a null check for this.inlpActions to prevent the error
      const currentAction = this.inlpActions && Array.isArray(this.inlpActions)
        ? this.inlpActions.find(action =>
          action.from === actionIdentifier.from &&
          action.to === actionIdentifier.to &&
          action.replaceText === actionIdentifier.replaceText
        )
        : null;

      // Store action for undo
      if (window.appContext) {
        window.appContext.pushToUndoStack({
          type: 'accept',
          originalText: this.highlightText,
          newText: this.replaceText,
          from: this.from,
          to: this.to,
          fullLine: this.fullLine,
          actionData: currentAction || {
            // Fallback data if currentAction is null
            from: this.from,
            to: this.to,
            highlightText: this.highlightText,
            replaceText: this.replaceText,
            fullLine: this.fullLine,
            id: this.id,
            action: this.action
          }
        });
      }

      // Find the current action index in the inlpActions array
      const currentIndex = this.inlpActions && Array.isArray(this.inlpActions)
        ? this.inlpActions.findIndex(action =>
          action.from === actionIdentifier.from &&
          action.to === actionIdentifier.to &&
          action.replaceText === actionIdentifier.replaceText
        )
        : -1;

      // Filter out the current action from inlpActions using the identifier
      const updatedActions = this.inlpActions && Array.isArray(this.inlpActions)
        ? this.inlpActions.filter(action => {
          // Check multiple properties to ensure we get the right action
          return !(
            action.from === actionIdentifier.from &&
            action.to === actionIdentifier.to &&
            action.replaceText === actionIdentifier.replaceText
          );
        })
        : [];

      // Store the updated actions in a local variable to ensure consistency
      const finalUpdatedActions = [...updatedActions];

      // Update the global state with the filtered actions
      const { setInlpActions } = window.appContext || {};
      if (setInlpActions) {
        // Use a callback to ensure we're working with the latest state
        setInlpActions(current => {
          // Make sure current is an array before filtering
          if (!Array.isArray(current)) return [];

          // Filter out the accepted action from the current state
          return current.filter(action => {
            return !(
              action.from === actionIdentifier.from &&
              action.to === actionIdentifier.to &&
              action.replaceText === actionIdentifier.replaceText
            );
          });
        });
      }


      // Calculate the length difference for position adjustment
      const lengthDifference = this.replaceText.length - this.highlightText.length;

      const isInsertAction = this.action === 'insert' || this.highlightText === '' || this.highlightText == null;
      // const isDeleteAction = !this.replaceText || this.replaceText.trim() === '';
      const isDeleteAction = this.replaceText === '';

      // Apply the change
      if (isInsertAction && !isDeleteAction) {
        // Insert action (empty highlightText, non-empty replaceText)
        const transaction = globalView.state.update({
          changes: {
            from: this.from,
            to: this.from,
            insert: this.replaceText
          },
          selection: {
            anchor: this.from + this.replaceText.length
          },
          annotations: Transaction.userEvent.of('input.type')
        });
        globalView.dispatch(transaction);
      } else if (isDeleteAction) {
        // Delete action (replaceText is empty, highlightText is not)
        const transaction = globalView.state.update({
          changes: {
            from: this.from,
            to: this.to,
            insert: ''
          },
          selection: {
            anchor: this.from
          }
        });
        globalView.dispatch(transaction);
      } else {
        // Replace action
        const transaction = globalView.state.update({
          changes: {
            from: this.from,
            to: this.to,
            insert: this.replaceText
          },
          selection: {
            anchor: this.from + this.replaceText.length
          }
        });
        globalView.dispatch(transaction);
      }

      // Update positions of remaining actions based on the change we just made
      const adjustedActions = finalUpdatedActions.map(action => {
        // If the action is after the current change, adjust its position
        if (action.from > this.to) {
          return {
            ...action,
            from: action.from + lengthDifference,
            to: action.to + lengthDifference,
            fullLine: action.fullLine || this.fullLine,
            id: action.id || `inlp-${action.from}-${action.to}-${Date.now()}`
          };
        }
        // If the action overlaps with the current change, we need special handling
        else if (action.from < this.to && action.to > this.from) {
          // This is a complex case that might need custom logic depending on your requirements
          // For now, we'll just preserve the action without changes
          return {
            ...action,
            fullLine: action.fullLine || this.fullLine,
            id: action.id || `inlp-${action.from}-${action.to}-${Date.now()}`
          };
        }
        // Otherwise, return the action unchanged
        return {
          ...action,
          fullLine: action.fullLine || this.fullLine,
          id: action.id || `inlp-${action.from}-${action.to}-${Date.now()}`
        };
      });

      // Update the editor with the adjusted actions
      globalView.dispatch({
        effects: [
          inlpEffect.of(adjustedActions)
        ]
      });

      tooltip.remove();

      // Navigate to the next action if there is one
      this.navigateToNextAction(currentIndex, adjustedActions);
    };


    tooltip.querySelector('.reject').onclick = () => {
      console.log("Reject button clicked");
      if (!globalView) {
        console.error("globalView is not available");
        return;
      }
      if (globalView?.state?.facet(EditorState.readOnly)) {
        tooltip.remove();
        return;
      }

      // Create a unique identifier for this action
      const actionIdentifier = {
        from: this.from,
        to: this.to,
        replaceText: this.replaceText,
        id: this.id
      };

      // Find the current action in the inlpActions array
      const currentAction = this.inlpActions && Array.isArray(this.inlpActions)
        ? this.inlpActions.find(action =>
          action.from === actionIdentifier.from &&
          action.to === actionIdentifier.to &&
          action.replaceText === actionIdentifier.replaceText
        )
        : null;

      // Store action for undo
      if (window.appContext) {
        window.appContext.pushToUndoStack({
          type: 'reject',
          originalText: this.highlightText,
          newText: this.replaceText,
          from: this.from,
          to: this.to,
          fullLine: this.fullLine,
          actionData: currentAction
        });
      }

      // Find the current action index in the inlpActions array
      const currentIndex = this.inlpActions && Array.isArray(this.inlpActions)
        ? this.inlpActions.findIndex(action =>
          action.from === actionIdentifier.from &&
          action.to === actionIdentifier.to &&
          action.replaceText === actionIdentifier.replaceText
        )
        : -1;

      // Filter out the current action from inlpActions using the identifier
      const updatedActions = this.inlpActions && Array.isArray(this.inlpActions)
        ? this.inlpActions.filter(action => {
          // Check multiple properties to ensure we get the right action
          return !(
            action.from === actionIdentifier.from &&
            action.to === actionIdentifier.to &&
            action.replaceText === actionIdentifier.replaceText
          );
        })
        : [];

      // Store the updated actions in a local variable to ensure consistency
      const finalUpdatedActions = [...updatedActions];

      // Update the global state with the filtered actions
      const { setInlpActions } = window.appContext || {};
      if (setInlpActions) {
        // Use a callback to ensure we're working with the latest state
        setInlpActions(current => {
          // Filter out the rejected action from the current state
          return current.filter(action => {
            return !(
              action.from === actionIdentifier.from &&
              action.to === actionIdentifier.to &&
              action.replaceText === actionIdentifier.replaceText
            );
          });
        });
      }

      // Create a transaction that will be undoable
      try {
        // For reject actions, we want to preserve the current text but make the change undoable
        const transaction = globalView.state.update({
          changes: {
            from: this.from,
            to: this.to,
            insert: globalView.state.doc.sliceString(this.from, this.to)
          },
          annotations: Transaction.userEvent.of('input.ignore')
        });


        globalView.dispatch(transaction);
      } catch (error) {
        console.error("Error during rejection handling:", error);
      }

      // Update the editor with the adjusted actions
      globalView.dispatch({
        effects: inlpEffect.of(finalUpdatedActions)
      });

      tooltip.remove();
      console.log("Tooltip removed");

      // Navigate to the next action if there is one
      this.navigateToNextAction(currentIndex, finalUpdatedActions);
    };

    // Add Ignore All functionality if duplicates exist
    if (hasDuplicates && tooltip.querySelector('.ignore-all')) {
      tooltip.querySelector('.ignore-all').onclick = () => {
        console.log("Ignore All button clicked");
        if (!globalView) {
          console.error("globalView is not available");
          return;
        }
        if (globalView?.state?.facet(EditorState.readOnly)) {
          tooltip.remove();
          return;
        }

        // Get all actions with the same highlightText and replaceText
        const actionsToIgnore = [
          // Include the current action
          {
            from: this.from,
            to: this.to,
            highlightText: this.highlightText,
            replaceText: this.replaceText
          },
          // Include all duplicates
          ...duplicates
        ];

        console.log(`Ignoring all ${actionsToIgnore.length} occurrences of "${this.highlightText}" → "${this.replaceText}"`);

        // Store actions for undo
        if (window.appContext) {
          // Store a single entry in the undo stack for the entire "ignore all" operation
          window.appContext.pushToUndoStack({
            type: 'ignore-all',
            actions: actionsToIgnore.map(action => ({
              originalText: action.highlightText,
              newText: action.replaceText,
              from: action.from,
              to: action.to,
              fullLine: action.fullLine || this.fullLine,
              actionData: {
                ...action,  // Ensure we capture ALL properties
                title: action.title || this.title,  // Explicitly include title
                group: action.group || this.group,  // Explicitly include group
                fullLine: action.fullLine || this.fullLine
              }
            }))
          });

          // Clear the redo stack when a new action is performed
          window.appContext.inlpRedoStack = [];
        }

        // Filter out all matching actions from inlpActions
        const updatedActions = this.inlpActions.filter(action => {
          return !actionsToIgnore.some(ignoreAction =>
            action.highlightText === ignoreAction.highlightText &&
            action.replaceText === ignoreAction.replaceText
          );
        });

        console.log("Original inlpActions length:", this.inlpActions.length);
        console.log("Filtered inlpActions length:", updatedActions.length);

        // Store the updated actions in a local variable to ensure consistency
        const finalUpdatedActions = [...updatedActions];

        // Update the global state with the filtered actions
        const { setInlpActions } = window.appContext || {};
        if (setInlpActions) {
          console.log("Updating global inlpActions state for Ignore All");
          // Use a callback to ensure we're working with the latest state
          setInlpActions(current => {
            const filtered = current.filter(action => {
              return !actionsToIgnore.some(ignoreAction =>
                action.highlightText === ignoreAction.highlightText &&
                action.replaceText === ignoreAction.replaceText
              );
            });
            console.log(`Removed ${current.length - filtered.length} actions with Ignore All`);
            return filtered;
          });
        } else {
          console.error("setInlpActions is not available in window.appContext");
        }

        // Make undoable changes for each action being ignored
        try {
          // Create a batch transaction for all ignore-all actions
          const changes = [];

          actionsToIgnore.forEach(action => {
            const from = action.from;
            const to = action.to;
            const text = globalView.state.doc.sliceString(from, to);

            // Add this change to our batch
            changes.push({
              from,
              to,
              insert: text
            });
          });

          // Dispatch a single transaction with all changes and a special annotation
          if (changes.length > 0) {
            globalView.dispatch({
              changes,
              annotations: Transaction.userEvent.of('input.ignore.all')
            });
          }
        } catch (error) {
          console.error("Error during ignore-all handling:", error);
        }

        // Update the editor with the adjusted actions
        globalView.dispatch({
          effects: inlpEffect.of(finalUpdatedActions)
        });

        tooltip.remove();
        console.log("Tooltip removed after Ignore All");

        // Navigate to the next action that's not of the same type
        const nextDifferentAction = finalUpdatedActions.find(action =>
          action.highlightText !== this.highlightText ||
          action.replaceText !== this.replaceText
        );

        if (nextDifferentAction) {
          const nextIndex = finalUpdatedActions.indexOf(nextDifferentAction);
          this.navigateToNextAction(nextIndex, finalUpdatedActions);
        }
      };
    }


    const handleClickOutside = (e) => {
      if (window.autoSaveInProgress) {
        return;
      }
      if (!tooltip.contains(e.target)) {
        tooltip.remove();
        document.removeEventListener('click', handleClickOutside);
      }
    };

    document.addEventListener('click', handleClickOutside);
  }

  // New method to navigate to the next action
  navigateToNextAction(currentIndex, updatedActions) {
    if (currentIndex === -1 || updatedActions.length === 0) return;

    // Find the next action to navigate to
    let nextIndex;

    // If currentIndex is valid, use the next action or wrap around
    if (currentIndex >= 0 && currentIndex < updatedActions.length) {
      nextIndex = currentIndex;
    } else if (currentIndex >= updatedActions.length) {
      nextIndex = 0; // Wrap around to the beginning
    } else {
      nextIndex = 0; // Default to first action
    }

    const nextAction = updatedActions[nextIndex];

    if (!nextAction) return;

    console.log("Original action from inlpActions:", {
      from: nextAction.from,
      to: nextAction.to,
      highlightText: nextAction.highlightText,
      replaceText: nextAction.replaceText,
      isEmpty: nextAction.highlightText === '' || nextAction.highlightText === ' '
    });

    // Create a longer delay to allow the current action to complete
    setTimeout(() => {
      // Focus on the next action
      if (globalView) {
        try {
          // Get the current document state
          // const docState = globalView.state.doc;
          const allWidgets = document.querySelectorAll('.inlp-action-widget.inlp-active-suggestion');
          allWidgets.forEach(widget => {
            widget.classList.remove('inlp-active-suggestion');
            // Reset styling to default
            // widget.style.textDecoration = 'underline solid #ffd700 3px';
            widget.style.backgroundColor = '#ffd700';
            widget.style.color = '#000';
            widget.style.fontWeight = 'normal';
            widget.style.boxShadow = 'none';
          });

          // Update the editor with the same actions but mark the current one as active
          const actionsWithActive = updatedActions.map(action => {
            if (action.from === nextAction.from && action.to === nextAction.to) {
              return { ...action, isActive: true };
            }
            return { ...action, isActive: false };
          });
          // First, scroll to the position
          globalView.dispatch({
            effects: EditorView.scrollIntoView(nextAction.from, { y: 'center' }, inlpEffect.of(actionsWithActive))
          });

          // Then, select the text range
          globalView.dispatch({
            selection: { anchor: nextAction.from, head: nextAction.to }
          });

          // Wait a tiny bit for the selection to be rendered
          setTimeout(() => {
            // Instead of creating a new tooltip, simulate a click on the decoration
            // This will use the exact same code path as manual clicks
            const inlpWidget = document.querySelector(`[data-inlp-id^="inlp-${nextAction.from}-${nextAction.to}"]`);

            if (inlpWidget) {
              // Add active class
              inlpWidget.classList.add('inlp-active-suggestion');
              // Apply active styling
              // inlpWidget.style.textDecoration = 'underline solid #000 3px';
              inlpWidget.style.backgroundColor = 'orange';
              // inlpWidget.style.color = 'white';
              inlpWidget.style.fontWeight = 'bold';
              inlpWidget.style.boxShadow = '0 0 5px rgba(255, 69, 0, 0.7)';

              // Simulate a click on the widget
              inlpWidget.click();
            } else {
              // If we can't find the widget by exact ID, try to find it by approximate position
              const allWidgets = document.querySelectorAll('.inlp-action-widget');
              let closestWidget = null;
              let minDistance = Infinity;

              allWidgets.forEach(widget => {
                const widgetId = widget.dataset.inlpId;
                if (widgetId) {
                  const parts = widgetId.split('-');
                  if (parts.length >= 3) {
                    const widgetFrom = parseInt(parts[1]);
                    const distance = Math.abs(widgetFrom - nextAction.from);

                    if (distance < minDistance) {
                      minDistance = distance;
                      closestWidget = widget;
                    }
                  }
                }
              });

              if (closestWidget && minDistance < 20) { // Only use if reasonably close
                // Add active class
                closestWidget.classList.add('inlp-active-suggestion');
                // Apply active styling
                // closestWidget.style.textDecoration = 'underline solid #000 3px';
                closestWidget.style.backgroundColor = 'orange';
                // closestWidget.style.color = 'white';
                closestWidget.style.fontWeight = 'bold';
                closestWidget.style.boxShadow = '0 0 5px rgba(255, 69, 0, 0.7)';

                closestWidget.click();
              } else {
                // Fallback: Create a synthetic click event for the InlpTooltipWidget
                // by creating a temporary instance and calling handleClick
                const tempWidget = new InlpTooltipWidget({
                  highlightText: nextAction.highlightText,
                  replaceText: nextAction.replaceText,
                  from: nextAction.from,
                  to: nextAction.to,
                  view: globalView,
                  fullLine: nextAction.fullLine,
                  changes: updatedActions.filter(change => change.fullLine === nextAction.fullLine),
                  inlpActions: updatedActions,
                  title: nextAction.title,
                  group: nextAction.group,
                  action: nextAction.action,
                  isActive: true
                });

                // Create a temporary span to pass to handleClick
                const tempSpan = document.createElement('span');
                tempSpan.dataset.inlpId = `inlp-${nextAction.from}-${nextAction.to}-${Date.now()}`;
                tempSpan.classList.add('inlp-active-suggestion');

                // Call handleClick directly
                tempWidget.handleClick({ stopPropagation: () => { } }, tempSpan);
              }
            }
          }, 50);
        } catch (error) {
          console.error("Error during navigation to next action:", error);
        }
      }
    }, 100);
  }


  eq(other) {
    return (
      this.id === other.id ||
      (this.highlightText === other.highlightText &&
        this.replaceText === other.replaceText &&
        this.from === other.from &&
        this.to === other.to)
    );
  }

  ignoreEvent() {
    return false;
  }
}



const deleteMark = (text, metadata, id, opType) => Decoration.widget({
  widget: new ChangeDeletedWidget(text, metadata),
  class: `ol-cm-change ol-cm-change-d ol-cm-change-user-${metadata.role.replace(/\s+/g, '-').toLowerCase()}`,
  side: 1,
  id,
  metadata: JSON.stringify(metadata),
  text,
  opType,
  attributes: {
    'data-from': metadata.from,
    'data-to': metadata.to,
    'data-user': metadata.Username,
    'data-role': metadata.role,
    ...(metadata.altTextKey ? { 'data-alttext-key': metadata.altTextKey } : {})
  }
})

// Change tracking effect and field
const insertionEffect = StateEffect.define();
const deletionEffect = StateEffect.define();
const commentEffect = StateEffect.define();
const clearCommentEffect = StateEffect.define();
const clearEffect = StateEffect.define();
// Retires one deletion widget resolved from the Track Changes panel - accepted
// (text stays deleted) or rejected (struck text put back). A deletion widget is
// a POINT decoration, so the point clearCommentEffect that removeComment sends
// cannot describe it and the 'd' branch of changeField only honours clearEffect
// ('r') ranges; this effect is the only way to drop one on demand.
// Carries {from, to, text} in NEW-doc coordinates; matched on text as well as
// position so an unrelated deletion at the same point is not removed with it.
const rejectDeletionEffect = StateEffect.define();
const inlpEffect = StateEffect.define();


// Position synchronization utility
const syncInlpPositions = (actions, doc) => {
  return actions.map(action => {
    const docLength = doc.length;

    let correctedFrom = Math.max(0, Math.min(action.from, docLength));
    let correctedTo = Math.max(correctedFrom, Math.min(action.to, docLength));

    // Special handling for insert actions
    if (
      (action.highlightText === '' || action.highlightText === ' ') &&
      typeof action.originalFrom === 'number'
    ) {
      correctedFrom = action.originalFrom;
      correctedTo = action.originalFrom;
    } else if (
      correctedFrom < correctedTo &&
      action.highlightText &&
      action.highlightText.trim() !== ''
    ) {
      const currentText = doc.sliceString(correctedFrom, correctedTo);

      if (currentText !== action.highlightText) {
        // Search in nearby area (±50 chars)
        const searchRadius = 50;
        const searchStart = Math.max(0, correctedFrom - searchRadius);
        const searchEnd = Math.min(docLength, correctedTo + searchRadius);
        const searchArea = doc.sliceString(searchStart, searchEnd);

        const escapeRegExp = s =>
          s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

        let pattern = escapeRegExp(action.highlightText);
        if (/^[A-Za-z]+$/.test(action.highlightText)) {
          pattern = `(?<![A-Za-z])${pattern}(?![A-Za-z])`;
        }

        let bestIndex = -1;
        let bestDistance = Infinity;

        try {
          const re = new RegExp(pattern, 'g');
          let match;

          while ((match = re.exec(searchArea)) !== null) {
            const absoluteIndex = searchStart + match.index;
            const distance = Math.abs(absoluteIndex - action.from);

            if (distance < bestDistance) {
              bestDistance = distance;
              bestIndex = absoluteIndex;
            }
          }
        } catch {
          console.log('Invalid regex pattern.');
        }

        if (bestIndex !== -1) {
          correctedFrom = bestIndex;
          correctedTo = correctedFrom + action.highlightText.length;
        } else {
          // Final fallback: simple indexOf
          const fallbackIndex = searchArea.indexOf(action.highlightText);
          if (fallbackIndex !== -1) {
            correctedFrom = searchStart + fallbackIndex;
            correctedTo = correctedFrom + action.highlightText.length;
          }
        }
      }
    }

    return {
      ...action,
      from: correctedFrom,
      to: correctedTo,
      positionSynced:
        correctedFrom !== action.from || correctedTo !== action.to
    };
  }).filter(
    action => action.from <= action.to && action.to <= doc.length
  );
};


const createInlpField = (view, inlpActions) => {
  globalView = view;

  return StateField.define({
    create() {
      return Decoration.none;
    },
    update(decorations, tr) {
      decorations = decorations.map(tr.changes);

      for (let effect of tr.effects) {
        if (effect.is(inlpEffect)) {
          const builder = new RangeSetBuilder();
          const currentActions = effect.value || [];

          // Synchronize positions with current document
          const syncedActions = syncInlpPositions(currentActions, tr.newDoc);

          // Sort actions by 'from' position to avoid the RangeSetBuilder error
          const sortedActions = [...syncedActions].sort((a, b) => {
            // First sort by 'from' position
            if (a.from !== b.from) {
              return a.from - b.from;
            }
            // If 'from' positions are equal, sort by 'to' position
            return a.to - b.to;
          });

          sortedActions.forEach(action => {
            const id = action.id || `inlp-${action.from}-${action.to}-${Date.now()}`;
            const widget = new InlpTooltipWidget({
              highlightText: action.highlightText,
              replaceText: action.replaceText,
              from: action.from,
              to: action.to,
              view,
              fullLine: action.fullLine,
              changes: sortedActions.filter(change => change.fullLine === action.fullLine),
              inlpActions: sortedActions,
              id,
              title: action.title,
              group: action.group,
              action: action.action,
              isUndoRedo: action.isUndoRedo,
              positionSynced: action.positionSynced
            });

            // Only add decoration if the range is valid
            if (action.from <= action.to && action.to <= tr.newDoc.length) {
              builder.add(action.from, action.to, Decoration.widget({
                widget,
                id: action.id
              }));
            }
          });

          decorations = builder.finish();
        }
      }
      return decorations;
    },
    provide: f => EditorView.decorations.from(f),
  });
};

const changeField = StateField.define({
  create(state) {
    if (state.doc.length > 0)
      return loadTrackChanges(state);
    else
      return Decoration.none;
  },
  update(decorations, tr) {
    let builder = new RangeSetBuilder();

    // Detect if this is a single character deletion
    let isSingleCharDeletion = true;
    let totalChanges = 0;
    tr.changes.iterChanges((fromA, toA, fromB, toB, inserted) => {
      totalChanges++;
      if (toA - fromA > 1 || inserted.length > 0) {
        isSingleCharDeletion = false;
      }
    });

    // Determine whether we should short-circuit to the simple mapping algorithm
    const userEvent = tr.annotation(Transaction.userEvent) || '';
    const isRejectFromTrack = userEvent === 'reject' || userEvent === 'deletereject';

    // If it's a single character deletion and only one change OR the change is a
    // track-side rejection, use simple mapping and skip the robust patch logic.
    if ((isSingleCharDeletion && totalChanges === 1) || isRejectFromTrack) {
      if (window?.trackChangesDebug) {
        console.log('[track-debug] Using simple decorations.map for change', { userEvent, isSingleCharDeletion, totalChanges });
      }
      decorations = decorations.map(tr.changes);
      if (isRejectFromTrack && window?.trackChangesDebug) {
        try {
          let counts = { d: 0, i: 0, c: 0 };
          decorations.between(0, tr.newDoc.length, (f, t, value) => {
            const cls = value.spec.class || '';
            if (cls.includes('ol-cm-change-d')) counts.d++;
            else if (cls.includes('ol-cm-change-i')) counts.i++;
            else if (cls.includes('ol-cm-change-c')) counts.c++;
          });
          console.log('[track-debug] Decorations after simple mapping for reject', counts);
        } catch (e) { }
      }
    } else {
      // Otherwise use the robust patch logic for multi-character changes
      let replacedRanges = [];
      tr.changes.iterChanges((fromA, toA, fromB, toB, inserted) => {
        if (toA > fromA) {
          replacedRanges.push({ from: fromA, to: toA });
        }
      });

      // Helper: check if a decoration overlaps a replaced range
      // function isDecorationOverlapped(from, to) {
      //   return replacedRanges.some(r => !(to <= r.from || from >= r.to));
      // }

      // Map decorations, but for those that are overlapped, split/truncate as needed
      let mappedDecorations = [];
      decorations.between(0, decorations.length ? decorations.length : (tr.startState ? tr.startState.doc.length : 0), (from, to, value) => {
        let overlapped = false;
        for (const r of replacedRanges) {
          if (from < r.from && to > r.to) {
            // Decoration fully covers replaced range, split into two
            mappedDecorations.push({ from, to: r.from, value });
            mappedDecorations.push({ from: r.to, to, value });
            overlapped = true;
            break;
          } else if (from < r.from && to > r.from && to <= r.to) {
            // Decoration ends inside replaced range, truncate
            mappedDecorations.push({ from, to: r.from, value });
            overlapped = true;
            break;
          } else if (from >= r.from && from < r.to && to > r.to) {
            // Decoration starts inside replaced range, truncate
            mappedDecorations.push({ from: r.to, to, value });
            overlapped = true;
            break;
          } else if (from >= r.from && to <= r.to) {
            // Decoration fully inside replaced range, drop it
            overlapped = true;
            break;
          }
        }
        if (!overlapped) {
          mappedDecorations.push({ from, to, value });
        }
      });

      // Now, map the decorations through the changes
      // Sort mappedDecorations by from (and to) to ensure correct order for RangeSetBuilder
      mappedDecorations.sort((a, b) => a.from - b.from || a.to - b.to);
      let mappedSet = new RangeSetBuilder();
      for (const { from, to, value } of mappedDecorations) {
        mappedSet.add(from, to, value);
      }
      decorations = mappedSet.finish().map(tr.changes);
    }
    // --- END PATCH ---

    // Collect new decorations from effects
    let newDecorations = [];
    let clearDecorations = [];
    let rejectedDeletions = [];
    // An undo/redo carries the effects that invertedEffects stored for the
    // forward transaction, and their positions are in that transaction's old-doc
    // coordinates. CodeMirror coalesces adjacent transactions into one history
    // event, so effects stored by an earlier member of the group are stale
    // against the composed change - and because these effects are declared with
    // a bare StateEffect.define() (no `map`), nothing remaps them. A backspace
    // joined with the typing that followed it therefore delivered
    // insertionEffect{from: 34477, to: 34478, text: 's'} to an undo that DELETED
    // 34471-34488, planting a 1-char insertion mark on untouched original text.
    // The builder re-derives insertion text from the doc slice, so the mark's
    // data-text always matched whatever character it landed on and the drift
    // guard could never see it.
    //
    // The invariant: an undo/redo may only re-mark text that it put back into
    // the document itself. Anything outside its own inserted ranges is stale.
    // deletionEffect is deliberately left alone - a misplaced deletion widget is
    // visible and rejectable, whereas dropping one loses the struck text.
    let historyInsertedRanges = null;
    if (userEvent === 'undo' || userEvent === 'redo') {
      historyInsertedRanges = [];
      tr.changes.iterChanges((fromA, toA, fromB, toB) => {
        if (toB > fromB) historyInsertedRanges.push({ from: fromB, to: toB });
      });
    }
    const isStaleHistoryEffect = (value) => {
      if (!historyInsertedRanges) return false;
      const from = value?.from, to = value?.to;
      if (typeof from !== 'number' || typeof to !== 'number') return false;
      return !historyInsertedRanges.some(r => from >= r.from && to <= r.to);
    };

    for (let effect of tr.effects) {
      if (effect.is(insertionEffect)) {
        if (isStaleHistoryEffect(effect.value)) {
          continue;
        }

        // If metadata is provided in the effect (e.g. from an undo operation), use it
        // Otherwise create new metadata with current time/user
        const meta = effect.value.metadata || { Username: userDetails?.username, role: userDetails.role, Date_Time: new Date().toLocaleString(), cmdtype: 'trackchanges', status: '' };
        newDecorations.push({ from: effect.value.from, to: effect.value.to, opType: 'i', decoration: changeMark(effect.value.text, meta), direction: '' });
      } else if (effect.is(deletionEffect)) {
        // Use metadata from the effect if provided (preserves altTextKey for alt text edits),
        // otherwise create default metadata
        const delMeta = effect.value.metadata || { Username: userDetails?.username, role: userDetails.role, Date_Time: new Date().toLocaleString(), cmdtype: 'trackchanges', status: '' };
        newDecorations.push({ from: effect.value.from, to: effect.value.to, opType: 'd', decoration: deleteMark(effect.value.text, delMeta), direction: effect.value.direction });
      }
      else if (effect.is(commentEffect)) {
        newDecorations.push({ from: effect.value.from, to: effect.value.to, opType: 'c', decoration: commentMark(effect.value.comment, effect.value.cid, effect.value.commentmeta), direction: '' });
      }
      else if (effect.is(clearCommentEffect)) {
        // newDecorations = newDecorations.filter(deco => !(deco.from === effect.value.from && deco.to === effect.value.to && deco.decoration.spec.class === 'ol-cm-change ol-cm-change-c'));
        clearDecorations.push({ from: effect.value.from, to: effect.value.to, opType: 'rc', direction: '' })
      }
      else if (effect.is(clearEffect)) {
        // Same staleness rule as insertionEffect: invertedEffects emits this
        // paired with the insertion, so a stale clear would trim or drop an
        // unrelated deletion widget that happens to sit at the stale position.
        if (isStaleHistoryEffect(effect.value)) {
          continue;
        }

        // Clear decorations within the range specified by the clearEffect
        clearDecorations.push({ from: effect.value.from, to: effect.value.to, opType: 'r', direction: '' });
      }
      else if (effect.is(rejectDeletionEffect)) {
        rejectedDeletions.push({
          from: effect.value.from,
          to: effect.value.to,
          text: effect.value.text || ''
        });
      }
    }
    if (isRejectFromTrack && window?.trackChangesDebug) {
      try {
        console.log('[track-debug] clearDecorations (post-effects):', clearDecorations);
        console.log('[track-debug] newDecorations (post-effects): count=', newDecorations.length);
      } catch (e) {}
    }

    // Preserve existing decorations
    decorations.between(0, tr.newDoc.length, (from, to, value) => {
      if (value.spec.class !== undefined) {
        let temptype = '';
        if (value.spec.class.includes('ol-cm-change-i'))
          temptype = 'i';
        else if (value.spec.class.includes('ol-cm-change-c'))
          temptype = 'c';
        else if (value.spec.class.includes('ol-cm-change-d'))
          temptype = 'd';

        if (temptype == 'd') {
          // Resolved from the Track Changes panel (accepted or rejected), so this
          // widget and its panel entry must go. On reject the widget has side:1, so
          // the insertion of the restored text maps it to the end of that text -
          // hence the inclusive range test; on accept nothing moves.
          const rejected = rejectedDeletions.some(
            r => from >= r.from && from <= r.to && (value.spec.text || '') === r.text
          );
          if (rejected) {
            return;
          }
          // Undo restored deleted text, but the deletion widget stays. Remove or trim
          // it by merging adjacent clear ranges and comparing restored vs struck length.
          const clears = clearDecorations
            .filter(d => d.opType === 'r')
            .slice()
            .sort((a, b) => a.from - b.from);
          // Merge into maximal contiguous intervals.
          const merged = [];
          for (const d of clears) {
            const last = merged[merged.length - 1];
            if (last && d.from <= last.to) last.to = Math.max(last.to, d.to);
            else merged.push({ from: d.from, to: d.to });
          }
          // The interval (if any) that covers this widget's point position.
          const iv = merged.find(m => m.from <= from && from <= m.to);
          // Cap restored length at this widget's own text, since a merged interval
          // can span several deletions and would otherwise leave a stale fragment.
          const restoredLen = iv
            ? Math.min(iv.to - iv.from, value.spec.text.length)
            : 0;

          if (!iv) {
            // No undo-restore touches this widget -> keep it untouched.
            newDecorations.push({
              from,
              to,
              opType: temptype,
              decoration: value,
              direction: ''
            });
          } else if (restoredLen < value.spec.text.length) {
            // Partial restore: peel restored chars off the front, keep the rest.
            // Build a fresh widget — never mutate value.spec (shared with undo history).
            const remainingText = value.spec.text.substring(restoredLen);
            let delMeta;
            try {
              delMeta = JSON.parse(value.spec.metadata || '{}');
            } catch (e) {
              delMeta = {};
            }
            newDecorations.push({
              from,
              to,
              opType: temptype,
              decoration: deleteMark(remainingText, delMeta, value.spec.id, value.spec.opType),
              direction: ''
            });
          }
          // else: entire text restored -> drop the widget.
        }
        else if (temptype === 'i') {
          let res = clearDecorations.filter(d => d.from === from && d.to === to);
          if (res === undefined || res.length === 0) {
            // Slice the current document: after mapping, from/to are tr.newDoc positions.
            const newStr = tr.newDoc.sliceString(from, to);
            const originalText = value.spec.attributes['data-text'] || '';

            // Did this transaction actually edit inside the mark's range?
            // Compare the edited span against [from,to] in new-doc coordinates.
            let touchedRange = false;
            tr.changes.iterChanges((fromA, toA, fromB, toB) => {
              if (fromB <= to && toB >= from) touchedRange = true;
            });

            // An insertion mark's text should change only if this edit touched it.
            // If an untouched mark's text differs, it drifted onto foreign text -> drop.
            if (newStr.length === 0) {
              // Inserted text fully gone -> drop, so undoing an insertion clears its entry.
            } else if (newStr === originalText) {
              newDecorations.push({ from, to, opType: temptype, decoration: value, direction: '' });
            } else if (touchedRange) {
              // Legitimate edit to inserted text: rebuild a fresh mark,
              // never mutating value.spec (shared with undo history).
              let decoration = value;
              let meta = null;
              try {
                meta = JSON.parse(value.spec.attributes['data-meta'] || 'null');
              } catch (e) {
                meta = null;
              }
              if (meta && meta.role) decoration = changeMark(newStr, meta);
              newDecorations.push({ from, to, opType: temptype, decoration, direction: '' });
            }
            // else: untouched mark whose text changed -> drifted -> drop it.
          }
        }
        else {
          let res = clearDecorations.filter(d => d.from === from && d.to === to);
          if ((res === undefined || res.length === 0) && (to > 0)) {
            newDecorations.push({
              from,
              to,
              opType: temptype,
              decoration: value,
              direction: ''
            });
          }
        }

      }
    });

    // Sort decorations by the `from` position
    // Deletions (point widgets, side:1) must come before insertions at the same position
    // to satisfy RangeSetBuilder's sorted order requirement
    newDecorations = newDecorations.sort((a, b) => {
      if (a.from !== b.from) return a.from - b.from;
      // At same position: deletions ('d') before insertions ('i') before comments ('c')
      const typePriority = { d: 0, i: 1, c: 2 };
      if (a.opType !== b.opType) return (typePriority[a.opType] || 0) - (typePriority[b.opType] || 0);
      // Among same type: prioritise decorations that carry an alttext key so they
      // become lastDecoration in the merge and the key is not lost
      const aHasAlt = !!(a.decoration.spec.attributes?.['data-alttext-key']);
      const bHasAlt = !!(b.decoration.spec.attributes?.['data-alttext-key']);
      if (aHasAlt !== bHasAlt) return aHasAlt ? -1 : 1;
      return a.to - b.to;
    });

    let lastFrom = -1, lastTo = -1, lastDecoration = {}, lastUser = '', lastRole = '';
    let finalDecorations = [];
    for (let { from, to, decoration, opType, direction } of newDecorations) {
      // Extract user info from decoration
      const userInfo = opType === 'd'
        ? JSON.parse(decoration.spec.metadata || '{}')
        : JSON.parse(decoration.spec.attributes['data-meta'] || '{}');

      const currentUser = userInfo.Username || '';
      const currentRole = userInfo.role || '';

      // Only merge if same position, type, AND same user/role
      if (from !== lastFrom || to !== lastTo ||
        decoration !== lastDecoration ||
        currentUser !== lastUser ||
        currentRole !== lastRole) {

        finalDecorations.push({
          from,
          to,
          decoration,
          opType,
          direction,
          user: currentUser,
          role: currentRole
        });

        lastFrom = from;
        lastTo = to;
        lastDecoration = decoration;
        lastUser = currentUser;
        lastRole = currentRole;
      }
    }

    lastFrom = -1;
    lastTo = -1;
    let ind = 0;
    let lasttype = '';
    let text = '';
    // Buffer all merged entries, then sort by (from, startSide) before adding to builder.
    // This prevents the "Ranges must be added sorted by from position and startSide" error
    // that occurs when a deletion widget (side:1, startSide ~1e9) and insertion mark
    // (startSide ~-1e9) share the same from position.
    const builderEntries = [];
    const pushEntry = (type, from, to, text, mdata, deco) => {
      builderEntries.push({ type, from, to, text, mdata, deco });
    };
    for (let { from, to, decoration, opType, direction, user, role } of finalDecorations) {
      // Extract user info
      const currentUser = user || '';
      const currentRole = role || '';

      if (ind === 0) {
        lastFrom = from;
        lastTo = to;
        lastDecoration = decoration;
        lasttype = opType;
        lastUser = currentUser;
        lastRole = currentRole;
        text = textmap(opType, decoration, text);
      }
      // Add a condition to separate by user/role
      else if (opType !== lasttype || currentUser !== lastUser || currentRole !== lastRole) {
        let mdata = createmeta(lastDecoration, lastFrom, lastTo, lasttype);
        pushEntry(lasttype, lastFrom, lastTo, text, mdata, lastDecoration);
        lastFrom = from;
        lastTo = to;
        lastDecoration = decoration;
        lasttype = opType;
        lastUser = currentUser;
        lastRole = currentRole;
        text = textmap(opType, decoration, '');
      }
      else if (opType === 'd' && lasttype === 'd') {
        if (lasttype === 'd') {
          // Only merge if same user/role
          if (lastUser === currentUser && lastRole === currentRole) {
            if (lastFrom === from) {
              text = textmap(opType, decoration, text, direction);
            } else if (lastTo > from) {
              text = textmap(opType, decoration, text, direction);
            } else {
              let mdata = createmeta(lastDecoration, lastFrom, lastTo, lasttype);
              pushEntry(lasttype, lastFrom, lastTo, text, mdata, lastDecoration);
              lastFrom = from;
              lastTo = to;
              lastDecoration = decoration;
              lasttype = opType;
              lastUser = currentUser;
              lastRole = currentRole;
              text = textmap(opType, decoration, '');
            }
          } else {
            // Different user, don't merge
            let mdata = createmeta(lastDecoration, lastFrom, lastTo, lasttype);
            pushEntry(lasttype, lastFrom, lastTo, text, mdata, lastDecoration);
            lastFrom = from;
            lastTo = to;
            lastDecoration = decoration;
            lasttype = opType;
            lastUser = currentUser;
            lastRole = currentRole;
            text = textmap(opType, decoration, '');
          }
        }
      }
      else if (opType === 'i' && lasttype === 'i') {
        if (lasttype === 'i') {
          // Only merge if same user/role
          if (lastUser === currentUser && lastRole === currentRole) {
            if (lastFrom === from || lastTo >= from) {
              text = textmap(opType, decoration, text);
              if (lastTo < to)
                lastTo = to;
            } else {
              let mdata = createmeta(lastDecoration, lastFrom, lastTo, lasttype);
              pushEntry(lasttype, lastFrom, lastTo, text, mdata, lastDecoration);
              lastFrom = from;
              lastTo = to;
              lastDecoration = decoration;
              lasttype = opType;
              lastUser = currentUser;
              lastRole = currentRole;
              text = textmap(opType, decoration, '');
            }
          } else {
            // Different user, don't merge
            let mdata = createmeta(lastDecoration, lastFrom, lastTo, lasttype);
            pushEntry(lasttype, lastFrom, lastTo, text, mdata, lastDecoration);
            lastFrom = from;
            lastTo = to;
            lastDecoration = decoration;
            lasttype = opType;
            lastUser = currentUser;
            lastRole = currentRole;
            text = textmap(opType, decoration, '');
          }
        }
      }
      else {
        let mdata = createmeta(lastDecoration, lastFrom, lastTo, lasttype);
        pushEntry(lasttype, lastFrom, lastTo, text, mdata, lastDecoration);
        lastFrom = from;
        lastTo = to;
        lastDecoration = decoration;
        lasttype = opType;
        lastUser = currentUser;
        lastRole = currentRole;
        text = textmap(opType, decoration, '');
      }
      ind = ind + 1;
      if (finalDecorations.length === ind) {
        let mdata = createmeta(lastDecoration, lastFrom, lastTo, lasttype);
        pushEntry(lasttype, lastFrom, lastTo, text, mdata, lastDecoration);
      }
    }

    // Sort buffered entries by (from, startSide) so RangeSetBuilder receives them
    // in the correct order. Marks (insertions 'i', comments 'c') have startSide -1e9
    // and must come before widgets (deletions 'd') which have startSide 1e9 at the
    // same from position.
    // Sort: deletions (point widgets) must come BEFORE insertions (range marks) at the
    // same `from` position. After adding a mark(from, to) the builder advances its internal
    // cursor past `to`, making it impossible to add a widget at `from` afterward.
    const typePriority = { d: 0, i: 1, c: 2 };
    builderEntries.sort((a, b) => {
      if (a.from !== b.from) return a.from - b.from;
      const aPri = typePriority[a.type] ?? 1;
      const bPri = typePriority[b.type] ?? 1;
      if (aPri !== bPri) return aPri - bPri;
      return a.to - b.to;
    });

    // An insertion mark's text is, by definition, the document text it covers.
    // When the user types *inside* an existing insertion, the enclosing mark has
    // already grown over the typed characters by the time the insertionEffect for
    // them arrives, so the merge above concatenates those characters a second time
    // and data-text ends up longer than the range it describes. The next
    // transaction that doesn't touch the mark then sees text !== slice and the
    // drift guard drops the whole insertion. Re-deriving from the document keeps
    // data-text and the range in sync (and still lets a genuinely drifted mark,
    // which is dropped before it reaches here, be caught).
    const newDocLength = tr.newDoc.length;
    for (const entry of builderEntries) {
      if (entry.type === 'i') {
        const sliceFrom = Math.max(0, Math.min(entry.from, newDocLength));
        const sliceTo = Math.max(sliceFrom, Math.min(entry.to, newDocLength));
        entry.text = tr.newDoc.sliceString(sliceFrom, sliceTo);
      }
      builder = addbuilder(builder, entry.type, entry.from, entry.to, entry.text, entry.mdata, entry.deco);
    }

    // Publish the set we just built, not tr.startState's. Serializing the start
    // state left the review panel one transaction behind, so a change removed by
    // the last transaction of a flow (e.g. rejecting a deletion, after which
    // nothing else dispatches) stayed listed in the Track Changes tab.
    const nextDecorations = builder.finish();
    storeTrackChanges(nextDecorations, tr.newDoc);
    return nextDecorations;
  },
  provide: field => EditorView.decorations.from(field),
});

const createmeta = (decoration, from, to, type) => {
  const meta = {
    Username: '',
    role: '',
    Date_Time: new Date().toLocaleString(),
    cmdtype: '',
    status: ''
  }
  let metadata = {};
  if (type === 'd') {
    metadata = JSON.parse(decoration.spec.metadata)
  }
  else {
    metadata = JSON.parse(decoration.spec.attributes['data-meta'] ? decoration.spec.attributes['data-meta'] : '{}');
  }

  if (type === 'c') {
    meta.from = from;
    meta.to = to;
    meta.Username = metadata.Username;
    meta.role = '';
    meta.Date_Time = metadata.Date_Time;
    meta.cmdtype = 'Comments';
  } else {
    meta.Username = metadata.Username;
    meta.role = metadata.role;
    meta.Date_Time = metadata.Date_Time;
    meta.cmdtype = 'trackchanges';
    meta.status = '';
    meta.from = from;
    meta.to = to;
    // Preserve altTextKey from whichever source has it
    const altKey = type === 'd'
      ? metadata.altTextKey
      : (decoration.spec.attributes?.['data-alttext-key'] || metadata.altTextKey);
    if (altKey) meta.altTextKey = altKey;
  }
  return meta;
}

const textmap = (type, decoration, text, direction = '') => {
  if (type === 'd') {
    if (direction === 'B') {
      text = decoration.spec.text + text;
    }
    else
      text = text + decoration.spec.text;
  }
  else if (type === 'i')
    text = text + decoration.spec.attributes['data-text']
  else if (type === 'c')
    text = text + decoration.spec.attributes['data-comment']
  return text;
}


const addbuilder = (builder, type, lastFrom, lastTo, text, mdata, deco) => {
  if (!builder) {
    builder = new RangeSetBuilder();
  }

  // Ensure from and to are valid numbers and from <= to
  const safeFrom = typeof lastFrom === 'number' ? Math.max(0, lastFrom) : 0;
  const safeTo = typeof lastTo === 'number' ? Math.max(safeFrom, lastTo) : safeFrom;

  try {
    if (type === 'd') {
      builder.add(safeFrom, safeFrom, deleteMark(text, mdata, '', type));
    } else if (type === 'i') {
      builder.add(safeFrom, safeTo, changeMark(text, mdata));
    } else if (type === 'c') {
      const commentId = deco.decoration !== undefined
        ? deco.decoration.spec.attributes['data-id']
        : deco?.spec?.attributes['data-id'] || '';
      builder.add(safeFrom, safeTo, commentMark(text, commentId, mdata));
    }
  } catch (error) {
    console.error("Error adding decoration to builder:", error);
    // Create a new builder if the current one is in a bad state
    builder = new RangeSetBuilder();
  }

  return builder;
};

// Helper function to create decoration with proper metadata
function createDecoration(text, metadata, type) {
  if (type === 'd') {
    return deleteMark(text, metadata);
  } else if (type === 'i') {
    return changeMark(text, metadata);
  } else {
    return commentMark(text, metadata.cid || '', metadata);
  }
}
// Function to deserialize decorations
function deserializeDecorations(parsedData, docLength) {
  let decorations = [];
  let builder = new RangeSetBuilder(); // Initialize builder here

  if (!parsedData || !Array.isArray(parsedData)) {
    return builder.finish();
  }

  // Parse and collect all decorations
  for (let deco of parsedData) {
    if (!deco) continue;

    if (deco.type === 'insertion') {
      decorations.push({
        from: deco.from,
        to: deco.to,
        type: 'i',
        Username: deco.Username,
        text: deco.text,
        status: deco.status,
        Date_Time: deco.Date_Time,
        cmdtype: deco.cmdtype,
        decoration: deco.decoration,
        startSide: 0
      });
    } else if (deco.type === 'deletion') {
      decorations.push({
        from: deco.from,
        to: deco.to,
        type: 'd',
        Username: deco.Username,
        text: deco.text,
        status: deco.status,
        Date_Time: deco.Date_Time,
        cmdtype: deco.cmdtype,
        decoration: deco.decoration,
        startSide: 0
      });
    } else if (deco.cmdtype === 'Comments') {
      decorations.push({
        from: deco.from,
        to: deco.to,
        decoration: commentMark(deco.Cmdcomtents, deco.cid, {
          Username: deco.Username,
          text: deco.Cmdcomtents,
          status: '',
          Date_Time: deco.Date_Time,
          cmdtype: deco.cmdtype
        }),
        type: 'c',
        Username: deco.Username,
        text: deco.text,
        status: deco.status,
        Date_Time: deco.Date_Time,
        cmdtype: deco.cmdtype,
        startSide: 0
      });
    }
  }

  // Sort decorations by from position and type priority
  decorations.sort((a, b) => {
    if (a.from !== b.from) {
      return a.from - b.from;
    }
    const typePriority = { d: 1, i: 2, c: 3 };
    return typePriority[a.type] - typePriority[b.type];
  });

  let lastFrom = -1;

  // Add sorted decorations with error handling
  for (let deco of decorations) {
    if (!deco || deco.from < lastFrom) continue;

    try {
      let text = textmap(deco.type, deco.decoration, '');
      let mdata = createmeta(deco.decoration, deco.from, deco.to, deco.type);
      builder = addbuilder(builder, deco.type, deco.from, deco.to, text, mdata, deco);
      lastFrom = deco.from;
    } catch (error) {
      console.error("Error processing decoration:", error);
      continue;
    }
  }

  try {
    return builder.finish();
  } catch (error) {
    console.error("Error finishing builder:", error);
    return Decoration.none;
  }
}

// Function to serialize decorations
function serializeDecorations(decorations, doc) {
  let serialized = [];
  let serializedcomment = [];
  decorations.between(0, doc.length, (from, to, decoration) => {
    if (decoration.spec.class.includes('ol-cm-change-i')) {
      let mdata = JSON.parse(decoration.spec.attributes['data-meta']);
      let text = decoration.spec.attributes['data-text'];
      const iAltKey = decoration.spec.attributes?.['data-alttext-key'] || mdata.altTextKey;
      serialized.push({
        type: 'insertion',
        from,
        to,
        decoration: decoration,
        text: text,
        Username: mdata.Username,
        Date_Time: mdata.Date_Time,
        cmdtype: mdata.cmdtype,
        status: mdata.status,
        role: mdata.role,
        ...(iAltKey ? { altTextKey: iAltKey } : {})
      });
    } else if (decoration.spec.class.includes('ol-cm-change-d')) {
      let mdata = JSON.parse(decoration.spec.metadata);
      const dAltKey = decoration.spec.attributes?.['data-alttext-key'] || mdata.altTextKey;
      serialized.push({
        type: 'deletion',
        from,
        to,
        decoration: decoration,
        text: decoration.spec.text || mdata.text,
        Username: mdata.Username,
        Date_Time: mdata.Date_Time,
        cmdtype: mdata.cmdtype,
        status: mdata.status,
        role: mdata.role,
        ...(dAltKey ? { altTextKey: dAltKey } : {})
      });
    } else if (decoration.spec.class.includes('ol-cm-change-c')) {
      // serialized.push({ type: 'comment', from, to, });
      serializedcomment.push({ type: 'comment', from, to, decoration, comment: decoration.spec.attributes['data-comment'], cid: decoration.spec.attributes['data-id'], commentmeta: decoration.spec.attributes['data-meta'] });
    }
  });
  return { serialized, serializedcomment };
}

let Comment;
let TrackchangesArrayValue;
let docid;
let ActivityID;

// Publish a decoration set to the review panel. Kept separate from
// saveTrackChanges so changeField.update can publish the set it has just built,
// which isn't reachable through a state's field yet.
function storeTrackChanges(decorations, doc) {
  let serializedDecorations = serializeDecorations(decorations, doc);
  Comment = serializedDecorations.serializedcomment;
  TrackchangesArrayValue = serializedDecorations.serialized;
}

// Function to save the track changes
function saveTrackChanges(state) {
  storeTrackChanges(state.field(changeField), state.doc);
}

// Function to load the track changes
function loadTrackChanges(state) {
  let savedData = TrackchangesArrayValue;
  let commentsData = Comment;
  if (!Array.isArray(savedData))
    savedData = [];
  if (!Array.isArray(commentsData))
    commentsData = [];
  let data = [...commentsData, ...savedData];
  if (data.length === 0)
    return Decoration.none;
  else
    return deserializeDecorations(data);
}

const annotationsTheme = EditorView.baseTheme({
  '.cm-gutter-lint': {
    order: -1,
  },
})

// Example of a simple custom linter for TeX
const texLinter = linter(view => {
  let diagnostics = [];
  const docText = view.state.doc.toString();
  let errors = lintWorker.Parse(docText).errors;
  for (const error of errors) {
    diagnostics.push(
      {
        from: error.startPos,
        to: error.endPos,
        severity: error.type,
        message: error.text,
      }
    )
  }
  return diagnostics;
});

class HighlightWidget extends WidgetType {
  constructor(content, cid) {
    super();
    this.content = content;
    this.cid = cid;
  }

  eq(other) {
    return other.text === this.text && other.cid === this.cid;
  }

  // You can also add this check when widgets are initially created
  toDOM() {
    const span = document.createElement('span');
    span.className = 'inlp-action-widget';

    // Your existing toDOM logic...

    if (!this.isReadOnly) {
      span.onclick = (e) => {
        // Check DOMRect before handling click
        const rect = span.getBoundingClientRect();
        if (!isValidDOMRect(rect)) {
          console.log('Widget has invalid DOMRect, skipping click');
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        this.handleClick(e, span);
      };
    }

    return span;
  }


  ignoreEvent() {
    return false;
  }
}

const highlightMatcher = new MatchDecorator({
  regexp: /\[([^\]]+)\]\(([^)]+)\)/g,
  decoration: match => Decoration.replace({
    widget: new HighlightWidget(match[1], match[2])
  })
});

const placeholders = ViewPlugin.fromClass(class {
  constructor(view) {
    this.placeholders = highlightMatcher.createDeco(view);
  }
  update(update) {
    this.placeholders = highlightMatcher.updateDeco(update, this.placeholders);
  }
}, {
  decorations: instance => instance.placeholders,
  provide: plugin => EditorView.atomicRanges.of(view => {
    return view.plugin(plugin)?.placeholders || Decoration.none;
  })
});
const updateInlpDecorations = (inlpActions) => {
  return inlpEffect.of(inlpActions.map(action => ({
    ...action,
    from: action.from,
    to: action.to,
    highlightText: action.highlightText,
    replaceText: action.replaceText,
    fullLine: action.fullLine
  })));
};
export function extractBalancedBraces(str, startIdx) {
  let stack = 0;
  let content = '';
  let i = startIdx;

  while (i < str.length) {
    if (str[i] === '{') stack++;
    else if (str[i] === '}') stack--;

    content += str[i];

    if (stack === 0) break;
    i++;
  }

  return { content: content.slice(1, -1), endIdx: i };
}

// Place this at the top of your file (outside the component)
export function processHTMLString(htmlString) {
  const trackChanges = [];
  const comments = [];
  const inlpCorrection = [];
  const commandPattern = /\\(ins|del|cmt|inlpaction)\[/g;

  htmlString = htmlString.replace(/(highlightText":")(.*?)(","replacetext)/ig, (match, p1, p2, p3) => {
    let replacedValue = p2.replace(/\}/g, 'iccb');
    return p1 + replacedValue + p3;
  });

  let resultString = htmlString.replace(/\r\n/g, '\n');
  let position = 0;
  let match;

  while ((match = commandPattern.exec(resultString)) !== null && position < 10000) {
    position++;
    const fullMatchStart = match.index;
    const command = match[1];
    const remainingString = resultString.substring(fullMatchStart);
    const jsonMatch = remainingString.match(/\\(?:ins|del|cmt|inlpaction)\[([^\]]*)\]/);
    if (!jsonMatch) continue;
    const jsonString = jsonMatch[1];
    const jsonEndIndex = fullMatchStart + jsonMatch[0].length;
    let content = '';
    let fullMatchEnd = jsonEndIndex;
    if (resultString[jsonEndIndex] === '{') {
      const { content: extracted, endIdx } = extractBalancedBraces(resultString, jsonEndIndex);
      content = extracted;
      fullMatchEnd = endIdx + 1;
    }
    const fullMatch = resultString.substring(fullMatchStart, fullMatchEnd);

    try {
      let jsonStringModified = jsonString
        .replace(/itempslash"/g, '\\"')
        .replace(/\\_/g, '_')
        .replace(/\\\$/g, '```')
        .replace(/\\%/g, '\\\\%')
        .replace(/\\#/g, '#')
        .replace(/iocb/g, '{')
        .replace(/iccb/g, '}')
        .replace(/iosbicsb/g, '{}')
        .trim();

      let valueObj;
      try {
        if (!jsonStringModified.startsWith('{') && !jsonStringModified.startsWith('[')) {
          if (!jsonStringModified.startsWith('"')) {
            jsonStringModified = `"${jsonStringModified}"`;
          }
        }
        valueObj = JSON.parse(jsonStringModified);
      } catch (e) {
        valueObj = { raw: jsonString };
      }

      valueObj.from = fullMatchStart;
      let toPosition;

      switch (command) {
        case 'del':
          resultString = resultString.slice(0, fullMatchStart) + resultString.slice(fullMatchEnd);
          toPosition = fullMatchStart;
          valueObj.to = toPosition;
          trackChanges.push(valueObj);
          break;
        case 'ins':
          resultString = resultString.slice(0, fullMatchStart) + content + resultString.slice(fullMatchEnd);
          toPosition = fullMatchStart + content.length;
          valueObj.to = toPosition;
          trackChanges.push(valueObj);
          break;
        case 'cmt':
          resultString = resultString.slice(0, fullMatchStart) + content + resultString.slice(fullMatchEnd);
          toPosition = fullMatchStart + content.length;
          valueObj.to = toPosition;
          comments.push(valueObj);
          break;
        case 'inlpaction':
          if (valueObj.action === "insert") {
            resultString = resultString.slice(0, fullMatchStart) + resultString.slice(fullMatchEnd);
            toPosition = fullMatchStart;
            valueObj.to = toPosition;
            inlpCorrection.push(valueObj);
          } else if (valueObj.action === "delete") {
            resultString = resultString.slice(0, fullMatchStart) + content + resultString.slice(fullMatchEnd);
            toPosition = fullMatchStart + content.length;
            valueObj.to = toPosition;
            inlpCorrection.push(valueObj);
          } else {
            resultString = resultString.slice(0, fullMatchStart) + content + resultString.slice(fullMatchEnd);
            toPosition = fullMatchStart + content.length;
            valueObj.to = toPosition;
            inlpCorrection.push(valueObj);
          }
          break;
        default:
          resultString = resultString.slice(0, fullMatchStart) + content + resultString.slice(fullMatchEnd);
          toPosition = fullMatchStart + content.length;
          valueObj.to = toPosition;
          break;
      }
      commandPattern.lastIndex = 0;
    } catch (error) {
      commandPattern.lastIndex = fullMatchEnd;
      continue;
    }
  }

  return {
    modifiedHTML: resultString,
    trackChanges,
    comments,
    inlpCorrection
  };
}


const CodeMirrorEditor = React.forwardRef((props, ref) => {
  const editorRef = useRef();
  const [state, setState] = useState(null);
  const [view, setView] = useState(null);
  const [lang, setLang] = useState('latex');
  const [visual, setVisual] = useState(true);
  const [fileContent, setFileContent] = useState('');
  const [trackContent, setTrackContent] = useState('')
  const [currentFile, setCurrentFile] = useState(null);
  const [stateDoc, setStateDoc] = useState({});
  const [selectedText, setSelectedText] = useState({ linenumber: 0 });
  const [showCommentButton, setShowCommentButton] = useState(true);
  const [comments, setComments] = useState({});
  const [textwrap, setTextWrap] = useState(true);
  const prevCid = useRef(null); // Ref for previous cid value
  const buttonResize = useRef(null);
  const subChildButton = useRef(null);
  const [ActivityName, setActivityName] = useState('')
  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0 });
  const [query, setQuery] = useState([]);
  const [showProgress, setShowprogress] = useState(false);
  const [progressDetails, setProgressDetails] = useState(false);
  const [ApiStatusRunning, setApiStatusRunning] = useState('')
  const [showGoToLineModal, setShowGoToLineModal] = useState(false);
  const [goToLineNumber, setGoToLineNumber] = useState('');
  const { userProfile, setReadOnly, trackChanges, setTrackChanges, savedFileContentData, trackAction, lineFocus, setViewDoc, authorQuery, setAuthorQuery, setTrackContents, authorQueryChange, trackChangeBlob, linepostion, commentValue, setCommentSidebar, commentDelete, fullData, setSaveTrigger, setEditorContent, setLayout, editorStyle, readOnly, setTrackfileComment, setHighlights, mainView, setMainView, setFileContentData, setCommentValue, setChangedComment, savedInlpActions, setInlpActions, inlpActions, trackChangesEnabled, isDataReady, alttextEnabled, altTextFlow } = useAppContextProvider();
  const trackChangesEnabledRef = useRef(trackChangesEnabled);
  const [initialTexContent, setInitialTexContent] = useState('');
  const [initialInlpActions, setInitialInlpActions] = useState([]);

  // Imperative handle for parent
  // Track toggle state: false = showing current, true = showing initial
  const showingInitialRef = useRef(false);
  const currentFileContentRef = useRef("");
  const currentInlpActionsRef = useRef([]);
  const currentTrackChangesRef = useRef([]);
  // Always-current snapshot of inlpActions for synchronous reads inside
  // CodeMirror update listeners (state closures there can be stale).
  const inlpActionsLiveRef = useRef([]);
  // console.log('<<trackChanges>>',trackChanges);

  useImperativeHandle(ref, () => ({
    showInitialTex: async () => {
      try {
        if (!showingInitialRef.current) {
          // Save current state
          currentFileContentRef.current = fileContent;
          currentInlpActionsRef.current = inlpActions;
          currentTrackChangesRef.current = trackChanges;
          // Save TrackchangesArrayValue for restoration
          if (typeof window !== 'undefined') {
            window._TrackchangesArrayValueBackup = window.TrackchangesArrayValue ? [...window.TrackchangesArrayValue] : [];
          }
          if (typeof TrackchangesArrayValue !== 'undefined') {
            window._TrackchangesArrayValueBackup = [...TrackchangesArrayValue];
          }
          let GetDetailDataString = localStorage.getItem(`${userProfile.docid}_init-getdetails`);
          var GetDetailData = JSON.parse(GetDetailDataString);
          let OutputPath = GetDetailData.Articledetails.OutputPath;
          // Always replace /36/ with /35/ in OutputPath
          OutputPath = OutputPath.replace(/\/36\//g, '/35/');
          let InputPath = OutputPath.replace("/Output/", "/Input/").replace(".xml", ".tex");
          // console.log("<init L2QA replacement>", InputPath);
          let InputFileBlobPath = `${InputPath}`;
          let url = `${pathconfig[packjson.environment].baseUrl}${pathconfig[packjson.environment].get_preview_file}`;
          const data = { InputPath: InputFileBlobPath };
          const response = await axios.post(url, data);
          const texContent = response.data.data || response.data;
          const result = processHTMLString(texContent);
          setInitialTexContent && setInitialTexContent(result.modifiedHTML);
          setInitialInlpActions && setInitialInlpActions(result.inlpCorrection);
          setFileContent(result.modifiedHTML);
          setInlpActions(result.inlpCorrection);
          // Force TrackchangesArrayValue and setTrackChanges to empty for initial state
          if (typeof window !== 'undefined') {
            window.TrackchangesArrayValue = [];
          }
          if (typeof TrackchangesArrayValue !== 'undefined') {
            TrackchangesArrayValue = [];
          }
          setTrackChanges([]);
          setReadOnly(true);
          showingInitialRef.current = true;
        } else {
          setFileContent(currentFileContentRef.current);
          setInlpActions(currentInlpActionsRef.current);
          setTrackChanges(currentTrackChangesRef.current);
          // Restore TrackchangesArrayValue when toggling back
          if (typeof window !== 'undefined' && window._TrackchangesArrayValueBackup) {
            window.TrackchangesArrayValue = [...window._TrackchangesArrayValueBackup];
          }
          if (typeof TrackchangesArrayValue !== 'undefined' && window._TrackchangesArrayValueBackup) {
            TrackchangesArrayValue = [...window._TrackchangesArrayValueBackup];
          }
          showingInitialRef.current = false;
        }
      } catch (error) {
        console.error(error);
      }
    },
    isShowingInitialTex: () => showingInitialRef.current,
    // Comments as they stand right now, for saving. changedComment is only
    // refreshed by the [Comment] effect below, which needs a later re-render
    // to fire (Comment is a module variable, and edits mutate comments in
    // place), so a save after the user goes idle — the session-timeout save —
    // would otherwise send stale comments. Returns null when there is nothing
    // reliable to build from, so the caller falls back to changedComment.
    getLatestComments: () => {
      if (showingInitialRef.current) return null;
      if (!Array.isArray(Comment) || !Array.isArray(commentValue?.comment)) return null;
      return CommentUpdateFunction(Comment, commentValue.comment);
    }
  }));

  const tagArray = ['\\\\textbf{', '\\\\textit{', '\\\\underline{', '\\\\href{', '\\\\begin{itemize}', '\\\\begin{enumerate}', '\\\\section{', '\\\\subsection{', '\\\\subsubsection', '\\\\paragraph', '\\\\subparagraph'];

  const inlpField = createInlpField(view, inlpActions);
  const authorQueryRef = useRef([]);
  const goButtonRef = useRef(null);
  const cancleButtonRef = useRef(null);

  // console.log(inlpActions, '<<inlpActions>>')

  // console.log('<{{trackChangesEnabled}}>', trackChangesEnabled);

  // Decide whether a CodeMirror `undo` transaction actually reverts the INLP
  // operation currently on top of the INLP undo stack. The native undo history
  // and the INLP stack can drift apart: any non-INLP edit (e.g. deleting a
  // whole paragraph) advances native history WITHOUT pushing an INLP entry.
  // Without this guard, undoing such an edit wrongly consumed the stale top
  // entry, which made an already-accepted suggestion reappear and partially
  // re-apply on an unrelated undo.
  const undoMatchesInlpAction = (tr, action) => {
    if (!action) return false;

    let insertedText = '';
    let insertedLen = 0;
    let deletedLen = 0;
    tr.changes.iterChanges((fromA, toA, fromB, toB, inserted) => {
      const text = inserted.toString();
      insertedText += text;
      insertedLen += text.length;
      deletedLen += toA - fromA;
    });
    const undoFootprint = insertedLen + deletedLen;

    if (action.type === 'manual-delete' || action.type === 'ignore-all') {
      // These restore whole spans of text, so the captured suggestion's
      // original text must appear inside what the undo actually restored.
      const original = action.actions?.[0]?.originalText || '';
      if (original) return insertedText.includes(original);
      // Insert-type suggestion (no original text): fall back to a size check.
      return undoFootprint <= 64;
    }

    // accept / reject: the forward op was a localised edit, so its undo must be
    // localised too. A whole-paragraph restoration is a different change and
    // must NOT consume this entry.
    const expected =
      (action.originalText?.length || 0) + (action.newText?.length || 0);
    const tolerance = Math.max(expected * 2, expected + 16);
    return undoFootprint <= tolerance;
  };

  const handleUndo = EditorView.updateListener.of((update) => {
    // console.log('<trackChangesEnabledRef.current>', trackChangesEnabledRef.current);

    if (userProfile.role === "Copy Editor" && !trackChangesEnabledRef.current) {
      return;
    }

    if (["Editor", "Author", "Copy Editor", "Master Copier"].includes(userProfile.role)) {
      if (update.transactions.some(tr => tr.docChanged)) {
        // Check if content has actually changed
        const newContent = update.state.doc.toString();
        if (newContent !== savedFileContentData) {
          setSaveTrigger(true);
        }

        update.transactions.forEach(tr => {
          let effects = [];
          const userEvent = tr.annotation(Transaction.userEvent);
          // Skip tracking changes for 'input.ignore' events
          if (userEvent === 'input.ignore' || userEvent === 'input.ignore.all') {
            return; // Skip this transaction entirely
          }
          // Skip auto-emitting track-change effects for alt-text saves.
          // updateAltTextWithTracking() already dispatches per-segment
          // insertion/deletion effects in the same transaction; letting this
          // listener also fire would add a giant whole-range insertion mark
          // on top of the segmented ones (visible in the review panel as one
          // entry containing the entire new alt text).
          if (userEvent === 'input.alttext') {
            return;
          }
          // Handle INLP undo/redo operations
          // Inside handleUndo function, replace the INLP undo/redo section with this:
          if (userEvent === 'undo' && window.appContext) {
            // Only consume an INLP undo entry when THIS undo actually reverts
            // that INLP operation (see undoMatchesInlpAction). Otherwise leave
            // the INLP stack and on-screen decorations untouched and fall
            // through to normal track-change handling.
            const topInlpAction = window.appContext.inlpUndoStack.length > 0
              ? window.appContext.inlpUndoStack[window.appContext.inlpUndoStack.length - 1]
              : null;

            // Process INLP undo operations
            if (topInlpAction && undoMatchesInlpAction(tr, topInlpAction)) {
              // Clear all INLP decorations first; the matching restore below
              // re-adds the ones that should remain.
              update.view.dispatch({
                effects: inlpEffect.of([])
              });

              const lastAction = window.appContext.popUndoStack();

              if (lastAction.type === 'accept') {
                // Check if this was an insert action (empty original text)
                const isInsertAction = lastAction.actionData?.action === 'insert' ||
                    !lastAction.originalText || lastAction.originalText === '';

                // Read where the undo ACTUALLY restored the original text, rather
                // than trusting the stored position. The stored position drifts
                // when the text was deleted and later re-inserted by intervening
                // undo/redo of unrelated edits (the undo-stack remap is asymmetric
                // for positions that sit inside a deleted span), which made the
                // restored suggestion appear several lines away from the word.
                let restoredFrom = lastAction.from;
                let foundRestore = false;
                tr.changes.iterChanges((fromA, toA, fromB) => {
                  if (!foundRestore) {
                    restoredFrom = fromB;
                    foundRestore = true;
                  }
                });

                // Wait for the native undo to complete
                setTimeout(() => {
                  // Get current document length after undo
                  const docLength = globalView ? globalView.state.doc.length : 0;
                  const baseFrom = foundRestore ? restoredFrom : lastAction.from;

                  // Create the restored action with appropriate properties
                  const restoredAction = {
                    ...lastAction.actionData,
                    highlightText: isInsertAction ? ' ' : lastAction.originalText,
                    // For insert actions at end of document, set range to be zero-width
                    from: isInsertAction && baseFrom >= docLength ? docLength : baseFrom,
                    to: isInsertAction && baseFrom >= docLength ? docLength :
                      (isInsertAction ? baseFrom : baseFrom + lastAction.originalText.length),
                    id: `inlp-${baseFrom}-${isInsertAction ? baseFrom : baseFrom + lastAction.originalText.length}-${Date.now()}`,
                    isUndoRedo: true,
                    title: lastAction.actionData.title,
                    group: lastAction.actionData.group,
                    replaceText: lastAction.newText,
                    wasInsertAction: isInsertAction,
                    // Add flag for end-of-document insertions
                    isEndOfDoc: isInsertAction && baseFrom >= docLength
                  };

                  // Before adding the restored action, check if there are other similar actions
                  window.appContext.setInlpActions(prev => {
                    const updatedActions = [...prev, restoredAction];

                    // Find potential duplicates
                    const duplicates = updatedActions.filter(action =>
                      action.highlightText === restoredAction.highlightText &&
                      action.replaceText === restoredAction.replaceText &&
                      !(action.from === restoredAction.from && action.to === restoredAction.to)
                    );

                    if (duplicates.length > 0) {
                      restoredAction.hasDuplicates = true;
                      restoredAction.duplicateCount = duplicates.length + 1;
                    }

                    return updatedActions;
                  });

                  update.view.dispatch({
                    effects: inlpEffect.of([restoredAction])
                  });
                }, 50);
              } else if (lastAction.type === 'reject') {
                // Read the real restored position from the undo (see the accept
                // branch above) so the decoration tracks the text even when the
                // stored position has drifted through delete + re-insert.
                let rejectFrom = lastAction.from;
                let rejectFound = false;
                tr.changes.iterChanges((fromA, toA, fromB) => {
                  if (!rejectFound) {
                    rejectFrom = fromB;
                    rejectFound = true;
                  }
                });
                const rejectBase = rejectFound ? rejectFrom : lastAction.from;
                const rejectWidth = (lastAction.originalText || '').length ||
                  (lastAction.to - lastAction.from);

                // For reject actions, restore the decoration
                const restoredAction = {
                  ...lastAction.actionData,
                  highlightText: lastAction.originalText,
                  from: rejectBase,
                  to: rejectBase + rejectWidth,
                  id: `inlp-${rejectBase}-${rejectBase + rejectWidth}-${Date.now()}`,
                  isUndoRedo: true
                };

                // Wait for the native undo to complete
                setTimeout(() => {
                  window.appContext.setInlpActions(prev => [...prev, restoredAction]);
                  update.view.dispatch({
                    effects: inlpEffect.of([restoredAction])
                  });
                }, 50);
              } else if (lastAction.type === 'ignore-all') {
                // For ignore-all actions, restore all the decorations
                const restoredActions = lastAction.actions.map(action => ({
                  ...action.actionData,  // This should include all original properties including title and group
                  highlightText: action.originalText,
                  from: action.from,
                  to: action.to,
                  id: `inlp-${action.from}-${action.to}-${Date.now()}`,
                  isUndoRedo: true
                }));

                // Wait for the native undo to complete
                setTimeout(() => {
                  // Add all restored actions back to the inlpActions array
                  window.appContext.setInlpActions(prev => [...prev, ...restoredActions]);

                  // Update the decorations
                  update.view.dispatch({
                    effects: inlpEffect.of(restoredActions)
                  });
                }, 50);
              } else if (lastAction.type === 'manual-delete') {
                // The user manually deleted text containing INLP suggestion(s)
                // and is now undoing it. The native undo restores the text; we
                // restore the suggestions' action/tooltip/highlight.
                const restoredActions = lastAction.actions.map(action => ({
                  ...action.actionData,
                  highlightText: action.originalText,
                  replaceText: action.newText,
                  from: action.from,
                  to: action.to,
                  fullLine: action.fullLine,
                  id: `inlp-${action.from}-${action.to}-${Date.now()}`,
                  isUndoRedo: true
                }));

                // Wait for the native undo to restore the text first.
                setTimeout(() => {
                  window.appContext.setInlpActions(prev => {
                    const existing = Array.isArray(prev) ? prev : [];
                    // Avoid duplicating a suggestion that is somehow still present.
                    const additions = restoredActions.filter(ra =>
                      !existing.some(a =>
                        a.from === ra.from &&
                        a.to === ra.to &&
                        a.highlightText === ra.highlightText &&
                        a.replaceText === ra.replaceText
                      )
                    );
                    return [...existing, ...additions];
                  });
                  update.view.dispatch({
                    effects: inlpEffect.of(restoredActions)
                  });
                }, 50);
              }
              // popUndoStack() already moved this entry onto the redo stack,
              // so we must not push it again (doing so caused phantom redos).
              // Return early to skip normal track-change handling for this
              // INLP undo.
              return;
            }
            // Not an INLP undo: fall through to normal track-change handling
            // below, leaving the INLP stack and decorations untouched.
          }
          else if (userEvent === 'redo') {
            // Process INLP redo operations
            if (window.appContext && window.appContext.inlpRedoStack.length > 0) {
              const lastUndone = window.appContext.inlpRedoStack.pop();
              // debugger
              if (lastUndone.type === 'accept') {
                // For accept actions, the text change is already handled by the editor's native redo
                // We need to ensure the INLP decoration stays removed AND track the change

                // Make sure the action stays removed from inlpActions
                if (window.appContext.setInlpActions) {
                  window.appContext.setInlpActions(prev =>
                    prev.filter(a => !(
                      a.from === lastUndone.actionData.from &&
                      a.to === lastUndone.actionData.to &&
                      a.highlightText === lastUndone.originalText &&
                      a.replaceText === lastUndone.newText
                    ))
                  );
                }

                // Add a special annotation to mark this as an INLP redo operation
                // This will be used to properly track the change
                update.view.dispatch({
                  annotations: Transaction.userEvent.of('inlp.redo.accept')
                });

                // Explicitly track this change as an insertion
                const from = lastUndone.from;
                const to = lastUndone.from + lastUndone.newText.length;
                const text = lastUndone.newText;

                update.view.dispatch({
                  effects: insertionEffect.of({ from, to, text })
                });
              } else if (lastUndone.type === 'reject') {
                // For reject actions, remove the INLP action again with precise filtering
                if (window.appContext.setInlpActions) {
                  window.appContext.setInlpActions(prev =>
                    prev.filter(a => !(
                      a.from === lastUndone.actionData.from &&
                      a.to === lastUndone.actionData.to &&
                      a.highlightText === lastUndone.actionData.highlightText &&
                      a.replaceText === lastUndone.actionData.replaceText
                    ))
                  );
                }
              } else if (lastUndone.type === 'ignore-all') {
                // For ignore-all actions, keep all the decorations removed
                if (window.appContext.setInlpActions) {
                  window.appContext.setInlpActions(prev => {
                    // Keep filtering out all actions that match any in the ignore-all group
                    return prev.filter(a => !lastUndone.actions.some(
                      ignoreAction =>
                        a.from === ignoreAction.from &&
                        a.to === ignoreAction.to &&
                        a.highlightText === ignoreAction.originalText
                    ));
                  });
                }
              } else if (lastUndone.type === 'manual-delete') {
                // Redoing a manual deletion removes the text again, so the
                // restored suggestions must be removed once more.
                if (window.appContext.setInlpActions) {
                  window.appContext.setInlpActions(prev =>
                    (Array.isArray(prev) ? prev : []).filter(a => !lastUndone.actions.some(
                      deletedAction =>
                        a.from === deletedAction.from &&
                        a.to === deletedAction.to &&
                        a.highlightText === deletedAction.originalText &&
                        a.replaceText === deletedAction.newText
                    ))
                  );
                }
              }

              // Update the decorations to reflect the current state
              update.view.dispatch({
                effects: inlpEffect.of(window.appContext.setInlpActions ? [] : [])
              });

              // Push back to undo stack
              window.appContext.inlpUndoStack.push(lastUndone);
            }

            // Skip the regular track changes handling for redo operations
            // that involve INLP actions to prevent duplicate tracking
            if (window.appContext && window.appContext.inlpRedoStack.length > 0) {
              return;
            }
          }
          if (userEvent === 'undo') return;
          const changeMap = new Map();

          // Check for delete.cut operation specifically
          const isCutOperation = userEvent === 'delete.cut' ||
            (userEvent === 'delete.selection' &&
              navigator.clipboard &&
              document.hasFocus());

          // Only MY OWN insertion may disappear without a trace - that is me
          // retracting an edit I have not submitted yet. Another user's tracked
          // insertion must still be recorded as my deletion, or removing a Copy
          // Editor's edit would erase it with nothing left for them to review.
          // Ambiguous ownership counts as someone else's: leaving a rejectable
          // marker is recoverable, silently dropping their change is not.
          const isMyInsertion = (value) => {
            let meta = null;
            try {
              meta = JSON.parse(value.spec.attributes?.['data-meta'] || 'null');
            } catch (e) {
              meta = null;
            }
            const owner = meta?.Username ?? value.spec.attributes?.['data-user'];
            const ownerRole = meta?.role ?? value.spec.attributes?.['data-role'];
            return !!owner
              && owner === userDetails?.username
              && ownerRole === userDetails?.role;
          };

          // Text that is itself MY tracked insertion was never in the original
          // document, so deleting it must NOT leave a deletion marker - removing
          // it just retires the insertion. Returns the part of [fromA, toA) that
          // still needs recording ('' when the whole span was my own insertion).
          //
          // The marks are read from tr.startState, NOT from the serialized
          // TrackchangesArrayValue snapshot: that snapshot is published while the
          // new state is being built, so by the time this listener runs it no
          // longer contains the insertion mark whose text this transaction just
          // deleted. tr.startState is also in the same coordinate space as
          // fromA/toA.
          const originalTextOnly = (fromA, toA) => {
            const doc = tr.startState.doc;
            const fullText = doc.sliceString(fromA, toA);

            let marks;
            try {
              marks = tr.startState.field(changeField);
            } catch (e) {
              return fullText;
            }

            const insertedSpans = [];
            marks.between(fromA, toA, (dFrom, dTo, value) => {
              if (value.spec.class?.includes('ol-cm-change-i') && isMyInsertion(value)) {
                insertedSpans.push({
                  from: Math.max(dFrom, fromA),
                  to: Math.min(dTo, toA)
                });
              }
            });
            if (insertedSpans.length === 0) return fullText;

            // Keep only the stretches NOT covered by my own insertion, so a delete
            // straddling the boundary still records the original text - or the
            // other user's inserted text - that it ate.
            insertedSpans.sort((a, b) => a.from - b.from);
            let kept = '';
            let cursor = fromA;
            for (const span of insertedSpans) {
              if (span.from > cursor) kept += doc.sliceString(cursor, span.from);
              cursor = Math.max(cursor, span.to);
            }
            if (cursor < toA) kept += doc.sliceString(cursor, toA);
            return kept;
          };

          // For undo operations - restore the deletion tracking
          if (userEvent === 'undo') {
            tr.changes.iterChanges((fromA, toA, fromB, toB) => {
              // Case 1: Text was deleted (fromA !== toA) - being restored as insertion
              if (fromA !== toA) {
                const deletedText = update.startState.doc.sliceString(fromA, toA);
                // When undoing a deletion, the deleted text comes back as an insertion
                effects.push(insertionEffect.of({ from: fromA, to: fromA + deletedText.length, text: deletedText }));
              }
              // Case 2: Text was inserted (fromB !== toB) - being removed
              // For a replace operation, this handles the insertion part being removed
              // We don't need to track this as deletion since it's being undone to original state
            });
          }
          // For redo operations - restore ALL tracking (both deletion and insertion for replace ops)
          else if (userEvent === 'redo') {
            tr.changes.iterChanges((fromA, toA, fromB, toB) => {
              // For a replace operation: fromA !== toA (deletion) AND fromB !== toB (insertion)

              // Part 1: Text deletion during redo
              if (fromA !== toA) {
                const redeletedText = update.startState.doc.sliceString(fromA, toA);
                // Track the deletion
                effects.push(deletionEffect.of({ from: fromA, to: toA, text: redeletedText, direction: '' }));
              }

              // Part 2: Text insertion during redo (important for replace operations!)
              if (fromB !== toB) {
                const reinsertedText = update.state.doc.sliceString(fromB, toB);
                // Track the insertion - this is crucial for replace operations
                effects.push(insertionEffect.of({ from: fromB, to: toB, text: reinsertedText }));
              }
            });
          }
          else if (userEvent !== 'undo' && userEvent !== 'redo') {
            tr.changes.iterChanges((fromA, toA, fromB, toB) => {
              if (fromA !== toA) {
                if (userEvent === undefined) {
                  const deletedText = update.startState.doc.sliceString(fromA, toA);
                  effects.push(deletionEffect.of({ from: fromA, to: toA, text: deletedText, direction: '' }));
                }
                else if (userEvent.includes('delete.selection') || userEvent.includes('delete.backward') || userEvent.includes('delete.forward') || userEvent.includes('input.type') || userEvent.includes('input.paste') || userEvent === 'delete.cut') {
                  const deletedText = originalTextOnly(fromA, toA);
                  if (deletedText.length > 0) {

                    // Special handling for delete.cut operation
                    if (userEvent === 'delete.cut' || isCutOperation) {
                      effects.push(deletionEffect.of({
                        from: fromA,
                        to: toA,
                        text: deletedText,
                        direction: '',
                        operation: 'delete.cut'
                      }));
                    }
                    else if (userEvent.includes('delete.backward'))
                      effects.push(deletionEffect.of({ from: fromA, to: toA, text: deletedText, direction: 'B' }));
                    else if (userEvent.includes('delete.forward'))
                      effects.push(deletionEffect.of({ from: fromA, to: toA, text: deletedText, direction: 'F' }));
                    else
                      effects.push(deletionEffect.of({ from: fromA, to: toA, text: deletedText, direction: '' }));
                  }
                }
                else if (userEvent.includes('input.replace')) {
                  // BUGFIX (Replace All): Find & Replace fires userEvent 'input.replace' /
                  // 'input.replace.all'. Effects are dispatched in a follow-up transaction
                  // AFTER the change is applied, so the deletion must use the NEW-doc
                  // coordinate (fromB). Using the old-doc fromA/toA worked for a single
                  // replace (fromA==fromB) but under Replace All each earlier replacement
                  // shifts positions, making fromA stale and the delete marker land elsewhere.
                  const deletedText = originalTextOnly(fromA, toA);
                  if (deletedText.length > 0) {
                    effects.push(deletionEffect.of({ from: fromB, to: fromB, text: deletedText, direction: '' }));
                  }
                }
              }
              if (fromB !== toB) {
                if (userEvent === 'deletereject') {
                  effects.push(clearCommentEffect.of({ fromB, fromB }));
                }
                else {
                  const text = update.state.doc.sliceString(fromB, toB);
                  effects.push(insertionEffect.of({ from: fromB, to: toB, text: text }));

                  // Store the insertion for potential redo
                  changeMap.set(`${fromB}-${toB}`, { type: 'insertion', text });
                }
              }
            });
          }

          if (effects.length > 0)
            update.view.dispatch({ effects });
        });
      }
    }
  });

  // New Extension: Handle Undo (Inverted Effects) for Track Changes
  const trackChangesUndoHandler = invertedEffects.of(tr => {
    if (!tr.docChanged) return [];

    // Correct guard
    if (tr.annotation(Transaction.addToHistory) === false) return [];

    const effects = [];

    tr.changes.iterChanges((fromA, toA, fromB, toB) => {
      if (fromA < toA) {
        // Collect the exact sub-ranges within the deleted span that were
        // tracked insertions. We must restore the insertion highlight ONLY on
        // those specific ranges on undo - not the entire deleted span. Marking
        // the whole span as an insertion is what caused a deleted paragraph
        // (that contained only a small tracked edit) to come back fully marked.
        const insertionRanges = [];
        // Also collect tracked DELETION markers inside the span. These are
        // zero-width widgets (the struck text lives in spec.text, not in the
        // document), so they were silently lost on undo - a deleted paragraph
        // that contained an accepted edit came back with the insertion but
        // without its matching deletion. Restore them too.
        const deletionPoints = [];
        tr.startState.field(changeField).between(fromA, toA, (from, to, value) => {
          const cls = value.spec.class || '';
          if (cls.includes('ol-cm-change-i')) {
            insertionRanges.push({
              from: Math.max(from, fromA),
              to: Math.min(to, toA),
              metadata: JSON.parse(value.spec.attributes?.['data-meta'] || '{}')
            });
          } else if (cls.includes('ol-cm-change-d')) {
            // Skip boundary widgets (at exactly toA). Under coalesced undo,
            // re-adding their intermediate snapshots would resurrect a stale
            // deletion; clearEffect handling in changeField removes them cleanly.
            if (from >= toA) return;
            let delMeta = null;
            try {
              delMeta = JSON.parse(value.spec.metadata || 'null');
            } catch (e) {
              delMeta = null;
            }
            deletionPoints.push({
              // The deletion widget is a point; keep it within the span.
              from: Math.min(Math.max(from, fromA), toA),
              text: value.spec.text || '',
              metadata: delMeta
            });
          }
        });

        // Always clear stale decorations across the restored span first, then
        // re-apply the previously-tracked insertions and deletions, leaving the
        // rest of the paragraph as plain text.
        effects.push(
          clearEffect.of({
            from: fromA,
            to: toA
          })
        );
        for (const range of insertionRanges) {
          if (range.to <= range.from) continue;
          const textRestored = tr.startState.doc.sliceString(range.from, range.to);
          effects.push(
            insertionEffect.of({
              from: range.from,
              to: range.to,
              text: textRestored,
              metadata: range.metadata
            })
          );
        }
        for (const del of deletionPoints) {
          if (!del.text) continue;
          effects.push(
            deletionEffect.of({
              from: del.from,
              to: del.from,
              text: del.text,
              metadata: del.metadata,
              direction: ''
            })
          );
        }
      }
    });

    return effects;
  });


  // This useEffect and useRef are used to update the real-time track changes state.
  // Without them, the state won't update properly because handleUndo is called outside the main function.
  // this useEffect and useRef help maintain the state without having to rewrite the existing logic, which was implemented by Naveen and should have been properly structured from the beginning.
  useEffect(() => {
    trackChangesEnabledRef.current = trackChangesEnabled;
  }, [trackChangesEnabled]);

  const undoneChangesField = StateField.define({
    create() {
      return [];
    },
    update(value, tr) {
      const userEvent = tr.annotation(Transaction.userEvent);

      if (userEvent === 'undo' && tr.docChanged) {
        const undoneChanges = [];
        tr.changes.iterChanges((fromA, toA, fromB, toB) => {
          if (fromA !== toA) {
            const text = tr.startState.doc.sliceString(fromA, toA);
            undoneChanges.push({ type: 'insertion', from: fromA, to: toA, text });
          }
          if (fromB !== toB) {
            const text = tr.startState.doc.sliceString(fromB, toB);
            undoneChanges.push({ type: 'deletion', from: fromB, to: toB, text });
          }
        });
        return undoneChanges;
      } else if (userEvent === 'redo') {
        // Clear after redo is processed
        return [];
      }
      return value;
    }
  });



  const handleReplace = EditorView.updateListener.of((update) => {
    if (update.transactions.some(tr => tr.docChanged)) {
      update.transactions.forEach(tr => {
        const userEvent = tr.annotation(Transaction.userEvent);
        if (userEvent === 'input.replace' || userEvent === 'input.replace.all') {
          const searchText = document.querySelector('input[name="search"]').value;
          const replaceText = document.querySelector('input[name="replace"]').value;
          console.log(state, 'state')
          if (state) {
            // Step 1: Count occurrences
            // BUGFIX: count in the LIVE editor document, not the stale React
            // `fileContent` state. `fileContent` only holds the content loaded
            // at startup/save, so a word created by an earlier find & replace
            // (or any unsaved edit) was never found -> wrong "No occurrences
            // found" on the second replace. tr.startState is the document as
            // it was just before this replace transaction, so the occurrence
            // count also includes the match the search panel just replaced.
            const docText = tr.startState.doc.toString();
            // BUGFIX: escape regex special characters so LaTeX searches like
            // "\\begin{figure}" are matched literally. Previously the raw search
            // string was used as a regex, so \b became a word boundary and { } ( ) etc.
            // were misread -> 0 matches -> "No occurrences found".
            const escapedSearch = searchText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const regex = new RegExp(escapedSearch, 'g');
            const count = (docText.match(regex) || []).length;
            if (count > 0) {
              // Step 2: Confirm replacement
              const userConfirmed = window.confirm(`Found ${count} occurrences. Do you want to replace them with '${replaceText}'?`);
              if (userConfirmed) {
                // Step 3: Replace text
                state.dispatch(
                  state.update({
                    changes: {
                      from: 0,
                      to: docText.length,
                      insert: docText.replace(regex, replaceText),
                    },
                  })
                );
                //setReplacementCount(count);
              }
            } else {
              alert('No occurrences found.');
            }
          }
        }
      });
    }
  });

  const latex = new LanguageSupport(LaTeXLanguage,
    [shortcuts(),
      documentCommands,
      documentEnvironmentNames,
    latexIndentService(),
    // EditorState.lineSeparator("\r\n"),
    EditorState.tabSize.of(4),
    metadata(),
    openAutocomplete(),
    ...completionSources.map(completionSource =>
      LaTeXLanguage.data.of({
        autocomplete: completionSource,
      })
    ),
      autocompleteTheme,
      changeField,
      handleUndo,
      trackChangesUndoHandler,
      handleReplace,
      trackChangesTheme,
    textwrap ? EditorView.lineWrapping : [],
    ]);

  useEffect(() => {
    docid = userProfile.docid;
    userDetails = userProfile;
    let a = JSON.parse(localStorage.getItem(`${docid}_init-getdetails`));
    let activityName = a.ActivtyDetails.ActivityName;
    // userProfile = a.ActivtyDetails.userDetails;
    ActivityID = a.ActivtyDetails.ActivityID;
    setActivityName(activityName);
  }, []);

  // Handle fullData changes
  useEffect(() => {
    if (fullData) {
      setFileContent(fullData.Content);
      setFileContentData(fullData.Content);
      setCurrentFile(fullData.Filename);
      if (userProfile?.role === 'Author' || userProfile?.role === 'Copy Editor') {
        setTrackContent(fullData.Content);
      }

      // if (userProfile?.role === 'Copy Editor') {
      //   setTrackContent(props.onlineData.Content);
      // }
    }
  }, [fullData, userProfile.role]);

  // Handle font size changes
  useEffect(() => {
    if (props.fontSize && editorRef.current) {
      const editor = editorRef.current.querySelector('.cm-editor');
      if (editor) {
        editor.style.fontSize = `${props.fontSize}px`;
      }
    }
  }, [props.fontSize]);

  // Handle text wrap changes
  useEffect(() => {
    if (props.textWrap) {
      setTextWrap(props.textWrap !== 'No');
    }
  }, [props.textWrap]);

  // Handle editor style value changes
  useEffect(() => {
    if (editorStyle) {
      setFileContent(editorStyle);
      setFileContentData(editorStyle);
    }
  }, [editorStyle]);

  useEffect(() => {
    Comment = commentValue?.comment;
    if (trackChangeBlob && Array.isArray(trackChangeBlob) && trackChangeBlob.length > 0) {
      // Only update if TrackchangesArrayValue is empty or undefined
      // This prevents overwriting local changes with stale props
      if (!TrackchangesArrayValue || TrackchangesArrayValue.length === 0) {
        TrackchangesArrayValue = trackChangeBlob;
        console.log('[TRACK CHANGES DEBUG] Updated TrackchangesArrayValue from trackChangeBlob:', TrackchangesArrayValue.length);
      } else {
        console.log('[TRACK CHANGES DEBUG] Ignoring trackChangeBlob because TrackchangesArrayValue has data:', TrackchangesArrayValue.length);
      }
    }
    loadTrackChanges(state);
    if (commentValue?.comment?.length > 0) {
      const lastComment = commentValue.comment[commentValue.comment.length - 1];
      highlightCommentedText(lastComment);
      const commentIndex = commentValue.comment[commentValue.changeIndex];
      if (commentIndex) {
        addComment(view, commentIndex.from, commentIndex.to, commentIndex.selectText, commentIndex.cid, JSON.stringify(commentIndex));
      }
    }
  }, [commentValue, trackChangeBlob]);

  function extractBalancedBraces(str, startIdx) {
    if (str[startIdx] !== '{') return { content: '', endIdx: startIdx };
    let depth = 0, endIdx = startIdx;
    for (; endIdx < str.length; endIdx++) {
      if (str[endIdx] === '{') depth++;
      else if (str[endIdx] === '}') depth--;
      if (depth === 0) break;
    }
    if (depth !== 0) return { content: '', endIdx: startIdx };
    return { content: str.slice(startIdx + 1, endIdx), endIdx: endIdx };
  }

  // Handle track content updates
  useEffect(() => {
    if (trackContent) {
      if (trackContent.length !== 0) {
        if (!editorRef.current) return;
        const result = processHTMLString(trackContent);
        console.log('Modified HTML String:\n', result.modifiedHTML);
        console.log('Extracted Array of track Objects:\n', result.trackChanges);
        console.log('Extracted Array of comment Objects:\n', result.comments);
        console.log('Extracted Array of inlp Objects:\n', result.inlpCorrection);

        const isInitialLoad = !savedInlpActions || Object.keys(savedInlpActions).length === 0;

        if (isInitialLoad) {
          console.log('Setting initial INLP actions from trackContent');
          setInlpActions(result.inlpCorrection);
        } else {
          console.log('Using saved INLP actions instead of trackContent');
          setInlpActions(savedInlpActions);
        }
        setTimeout(() => {
          setFileContent(result.modifiedHTML);
          setFileContentData(result.modifiedHTML);
          // TrackchangesArrayValue = result.trackChanges;
          TrackchangesArrayValue = result.trackChanges.length !== 0 ? result.trackChanges : TrackchangesArrayValue;
          Comment = result.comments;
          setTrackfileComment(result.comments);
          setTrackContents(result.modifiedHTML);
        }, 1000);
      }
    }
  }, [trackContent]);

  // Handle stateDoc changes
  useEffect(() => {
    const stateValue = async () => {
      if (stateDoc?.doc?.lines >= 1) {
        if (state.doc.length > 0) {
          await AQFinder(state.doc);
        }
      }
    };
    if (stateDoc) stateValue();
  }, [stateDoc]);

  // Handle line focus changes
  useEffect(() => {
    if (lineFocus) {
      if (lineFocus?.line || lineFocus?.position) {
        focusOnLineError(lineFocus);
      } else {
        focusOnLine(lineFocus);
      }
    }
  }, [lineFocus]);

  // Handle comment and track changes
  useEffect(() => {
    if (TrackchangesArrayValue) {
      let dataChange = TrackchangesArrayValue.map((item) => {
        return {
          ...item,
          Username: item.Username?.length === 0 ? userProfile.Name : item.Username,
          role: item.role?.length === 0 ? userProfile.roleName : item.role,
          ActivityID: (item.ActivityID?.length === 0 || item.ActivityID === undefined) ? ActivityID : item.ActivityID,
          Date_Time: item.type === 'insertion'
            ? JSON.parse(item.decoration?.spec.attributes['data-meta']).Date_Time
            : JSON.parse(item.decoration?.spec.metadata).Date_Time,
          cmdtype: 'trackchanges',
          status: ''
        }
      })
      let filter = dataChange.filter((item) => item.type === 'deletion' || item.type === 'insertion')
      setTrackChanges(filter);
    }
  }, [TrackchangesArrayValue]);

  // Handle comment deletion
  useEffect(() => {
    if (commentDelete) {
      // AuthorQueryDelete(commentDelete);
      removeHighlightedText(commentDelete);
      if (Object.keys(commentValue?.comment).length > 0) {
        const updatedRange = commentValue?.comment.filter((item) => item.cid !== commentDelete.cid);
        setTimeout(() => {
          let CommentsChange = { comment: updatedRange, changeIndex: null }
          setCommentValue(CommentsChange);
        }, 100);
      }
    }
  }, [commentDelete]);

  // Handle author query changes
  useEffect(() => {
    if (authorQueryChange) {
      AuthorQueryUpdate(authorQueryChange);
    }
  }, [authorQueryChange]);

  // Handle queries
  // useEffect(() => {    //- not required
  //   if (query?.length > 0) {
  //     setAuthorQuery(query); 
  //   }
  // }, [query]);

  // Handle track action

  useEffect(() => {
    window.appContext = {
      setInlpActions: (updater) => {
        if (typeof updater === 'function') {
          setInlpActions(current => {
            const updated = updater(current);
            return updated;
          });
        } else {
          setInlpActions(updater);
        }
      },
      // Add these new properties for undo/redo
      inlpUndoStack: [],
      inlpRedoStack: [],
      pushToUndoStack: (action) => {
        window.appContext.inlpUndoStack.push(action);
        window.appContext.inlpRedoStack = []; // Clear redo stack on new action
      },
      popUndoStack: () => {
        if (window.appContext.inlpUndoStack.length === 0) return null;
        const action = window.appContext.inlpUndoStack.pop();
        window.appContext.inlpRedoStack.push(action);
        return action;
      },
      popRedoStack: () => {
        if (window.appContext.inlpRedoStack.length === 0) return null;
        const action = window.appContext.inlpRedoStack.pop();
        window.appContext.inlpUndoStack.push(action);
        return action;
      }
    };

    return () => {
      delete window.appContext;
    };
  }, [setInlpActions]);


  useEffect(() => {
    if (!trackAction || !view) return;
    if (trackAction?.status === "Accept") {
      removeComment(view, trackAction?.list?.from, trackAction?.list?.to);
      // Accepting a deletion means the text stays gone and the marker retires.
      // removeComment only sends a point clearCommentEffect, which the 'd' branch
      // of changeField does not act on, so the widget has to be dropped
      // explicitly - otherwise the marker and its panel entry survive the accept.
      if (trackAction?.list?.type === 'deletion') {
        const struckText = trackAction?.list?.decoration?.spec?.text || '';
        if (struckText) {
          const at = trackAction?.list?.from;
          view.dispatch({
            effects: rejectDeletionEffect.of({ from: at, to: at, text: struckText })
          });
        }
      }
    } else {
      const from = trackAction?.list?.from;
      const to = trackAction?.list?.to;
      let transaction = {};

      if (trackAction?.list?.type === 'deletion') {
        removeComment(view, trackAction?.list?.from, trackAction?.list?.to);
        const restoredText = trackAction?.list?.decoration?.spec?.text || '';
        transaction = view.state.update({
          changes: {
            from: from,
            to: to,
            insert: restoredText
          },
          // Re-inserting the text is only half of a reject: the deletion widget
          // survives the position mapping (it is a point widget, not a range), so
          // it has to be dropped explicitly or the change stays in the panel and
          // the marker stays in the editor. Skipped when there is no struck text
          // to restore, since an empty matcher would match any empty-text widget.
          effects: restoredText
            ? [rejectDeletionEffect.of({
              from,
              to: from + restoredText.length,
              text: restoredText
            })]
            : [],
          annotations: Transaction.userEvent.of('deletereject')
        });
      } else {
        transaction = view.state.update({
          changes: { from, to },
          annotations: Transaction.userEvent.of('reject')
        });
      }

      view.dispatch(transaction);
    }
  }, [trackAction, view]);


  // ─── \alttext read-only extension ─────────────────────────────────────────
  /**
   * Balanced-brace reader: given `str` and the index of an opening `{`,
   * returns { content, start, end } where start/end are ABSOLUTE indices
   * into `str` (start = index after `{`, end = index of closing `}`).
   * Handles arbitrarily nested braces without regex.
   */
  function readBraceBlock(str, openIdx) {
    if (str[openIdx] !== '{') return null;
    let depth = 0;
    let i = openIdx;
    while (i < str.length) {
      if (str[i] === '{') depth++;
      else if (str[i] === '}') {
        depth--;
        if (depth === 0) {
          return { content: str.slice(openIdx + 1, i), start: openIdx + 1, end: i };
        }
      }
      i++;
    }
    return null; // unbalanced
  }

  /**
   * Scan `docText` and collect every absolute [from, to) range that is
   * covered by an \alttext macro, INCLUDING the macro keyword itself.
   * Returns an array of { from, to } objects (both inclusive of the full token).
   */
  function findAltTextRanges(docText) {
    const ranges = [];
    let searchFrom = 0;
    while (true) {
      const cmd = docText.indexOf('\\alttext', searchFrom);
      if (cmd === -1) break;
      // expect an optional [<language code>] then 3 brace groups:
      //   \alttext[en]{type}{variant}{text}
      let cursor = cmd + '\\alttext'.length;
      // skip optional whitespace
      while (cursor < docText.length && (docText[cursor] === ' ' || docText[cursor] === '\n' || docText[cursor] === '\r' || docText[cursor] === '\t')) cursor++;
      // skip the optional language-code argument when present. An unclosed
      // bracket, or one running past the first mandatory group, is malformed —
      // skip the macro rather than swallowing document text into the range.
      if (docText[cursor] === '[') {
        const close = docText.indexOf(']', cursor);
        const nextBrace = docText.indexOf('{', cursor);
        if (close === -1 || (nextBrace !== -1 && close > nextBrace)) { searchFrom = cmd + 1; continue; }
        cursor = close + 1;
        while (cursor < docText.length && (docText[cursor] === ' ' || docText[cursor] === '\n' || docText[cursor] === '\r' || docText[cursor] === '\t')) cursor++;
      }
      const g1 = docText.indexOf('{', cursor);
      if (g1 === -1) { searchFrom = cmd + 1; continue; }
      const b1 = readBraceBlock(docText, g1);
      if (!b1) { searchFrom = cmd + 1; continue; }
      let c2 = b1.end + 1;
      while (c2 < docText.length && (docText[c2] === ' ' || docText[c2] === '\n' || docText[c2] === '\r' || docText[c2] === '\t')) c2++;
      const g2 = docText.indexOf('{', c2);
      if (g2 === -1) { searchFrom = cmd + 1; continue; }
      const b2 = readBraceBlock(docText, g2);
      if (!b2) { searchFrom = cmd + 1; continue; }
      let c3 = b2.end + 1;
      while (c3 < docText.length && (docText[c3] === ' ' || docText[c3] === '\n' || docText[c3] === '\r' || docText[c3] === '\t')) c3++;
      const g3 = docText.indexOf('{', c3);
      if (g3 === -1) { searchFrom = cmd + 1; continue; }
      const b3 = readBraceBlock(docText, g3);
      if (!b3) { searchFrom = cmd + 1; continue; }
      // full macro: from cmd to closing brace of 3rd group (inclusive)
      ranges.push({ from: cmd, to: b3.end + 1 });
      searchFrom = b3.end + 1;
    }
    return ranges;
  }

  // StateField: compute mark decorations for every \alttext range
  const altTextDecorationField = StateField.define({
    create(state) {
      return buildAltTextDecorations(state.doc.toString());
    },
    update(decorations, tr) {
      if (tr.docChanged) {
        return buildAltTextDecorations(tr.newDoc.toString());
      }
      return decorations;
    },
    provide: f => EditorView.decorations.from(f),
  });

  function buildAltTextDecorations(docText) {
    const builder = new RangeSetBuilder();
    const ranges = findAltTextRanges(docText);
    const altMarkDecoration = Decoration.mark({ class: 'cm-alttext-readonly' });
    // RangeSetBuilder requires ranges to be added in ascending order
    for (const { from, to } of ranges) {
      if (from < to && to <= docText.length) {
        builder.add(from, to, altMarkDecoration);
      }
    }
    return builder.finish();
  }

  // TransactionFilter: block any change that touches an \alttext range
  const altTextTransactionFilter = EditorState.transactionFilter.of(tr => {
    if (!tr.docChanged) return tr;
    const userEvent = tr.annotation(Transaction.userEvent) || '';
    // Allow alttext saves, and track-change accept/reject dispatches
    if (userEvent === 'input.alttext' || userEvent === 'reject' || userEvent === 'deletereject') return tr;
    const docText = tr.startState.doc.toString();
    const protectedRanges = findAltTextRanges(docText);
    if (protectedRanges.length === 0) return tr;
    let blocked = false;
    tr.changes.iterChanges((fromA, toA) => {
      if (blocked) return;
      for (const pr of protectedRanges) {
        if (fromA < pr.to && toA > pr.from) {
          blocked = true;
          break;
        }
      }
    });
    if (blocked) return [];
    return tr;
  });

  // helper: return file name for alttext located at `pos` (or null)
  function getFileNameForAltPosition(docText, pos) {
  let match;

  // 1️⃣ Handle figure blocks
  const beginBlockRegex = /\\begin\{(figure\*?|sidewaysfigure\*?|altecom)\}[\s\S]*?\\end\{\1\}/gm;

  while ((match = beginBlockRegex.exec(docText)) !== null) {
    const start = match.index;
    const end = beginBlockRegex.lastIndex;

    if (pos >= start && pos < end) {
      const block = match[0];

      // includegraphics
      const incIdx = block.indexOf('\\includegraphics');
      if (incIdx !== -1) {
        const braceStart = block.indexOf('{', incIdx);

        if (braceStart !== -1) {
          let depth = 0;
          let i = braceStart;

          for (; i < block.length; i++) {
            if (block[i] === '{') depth++;
            else if (block[i] === '}') depth--;

            if (depth === 0) break;
          }

          return block.slice(braceStart + 1, i).trim();
        }
      }
    }
  }

  // 2️⃣ Handle inlinefigure (FIXED)
  let index = docText.indexOf('\\inlinefigure');

  while (index !== -1) {
    const braceStart = docText.indexOf('{', index);
    if (braceStart === -1) return null;

    // parse full inlinefigure block
    let depth = 0;
    let i = braceStart;

    for (; i < docText.length; i++) {
      if (docText[i] === '{') depth++;
      else if (docText[i] === '}') depth--;

      if (depth === 0) break;
    }

    const start = index;
    const end = i + 1;

    if (pos >= start && pos < end) {
      const block = docText.slice(braceStart + 1, i);

      // extract includegraphics filename
      const incMatch = block.match(/\\includegraphics(?:\[[^\]]*\])?\s*\{([^}]+)\}/);
      if (incMatch) {
        return incMatch[1].trim();
      }
    }

    index = docText.indexOf('\\inlinefigure', end);
  }

  return null;
}

  // ViewPlugin: intercept Ctrl+mousedown clicks inside \alttext ranges
  const altTextClickPlugin = ViewPlugin.fromClass(class {
    constructor(view) {
      this._handler = this._onMouseDown.bind(this);
      view.dom.addEventListener('mousedown', this._handler, true);
    }
    _onMouseDown(event) {
      const isCtrlPressed = event.ctrlKey || event.metaKey;

      // Determine doc position under the pointer
      const v = this._view;
      if (!v) return;
      const pos = v.posAtCoords({ x: event.clientX, y: event.clientY });
      if (pos === null) return;
      const docText = v.state.doc.toString();
      const ranges = findAltTextRanges(docText);
      const inside = ranges.some(r => pos >= r.from && pos < r.to);
      if (!inside) return;

      if (!isCtrlPressed) {
        toastWarning('Please edit in the imagelis with alttext , Ctrl + Click to open represent fig. in imagelist');
        return;
      }

      // figure out file name for this alttext
      const fileName = getFileNameForAltPosition(docText, pos);
      // Notify the host (Layout) to switch to the Image List tab
      if (typeof altTextClickCallback === 'function') {
        altTextClickCallback(fileName);
      } else {
        // Fallback: fire a custom DOM event the host can listen for
        document.dispatchEvent(new CustomEvent('alttext:clicked', { detail: { fileName }, bubbles: true }));
      }
    }
    update(update) {
      this._view = update.view;
    }
    destroy() {
      if (this._view) {
        this._view.dom.removeEventListener('mousedown', this._handler, true);
      }
    }
  });

  // CSS theme for grayed-out, cursor:not-allowed alttext blocks
  const altTextTheme = EditorView.baseTheme({
    '.cm-alttext-readonly': {
      color: '#999 !important',
      backgroundColor: 'rgba(200,200,200,0.18)',
      cursor: 'not-allowed',
      userSelect: 'none',
      pointerEvents: 'auto',
      borderRadius: '2px',
    },
    '&dark .cm-alttext-readonly': {
      color: '#666 !important',
      backgroundColor: 'rgba(100,100,100,0.18)',
    },
  });

  // Bundle all pieces into a single extension array. \alttext stays locked for
  // every role while the document is in the alt-text flow; only the roles that
  // work with alt text get the Ctrl+click jump to the Image List.
  const altTextReadOnlyExtension = [
    altTextDecorationField,
    altTextTransactionFilter,
    ...(alttextEnabled ? [altTextClickPlugin] : []),
    altTextTheme,
  ];
  // ──────────────────────────────────────────────────────────────────────────

  const readOnlyRanges = EditorView.decorations.compute(['doc'], state => {
    const decorations = []
    const regex = /\\(journalvolume|journalid|journalcode|journaltitle|abbjournaltitle|journalissue|issn|pissn|eissn|publishername|publisherloc|articletype|articleid|articlenumber|DOI|PII|received|revised|accepted|pubdate|copyrightstatement|copyrightyear|copyrightholder|copyrighttype|license)\{[^}]*\}|\\\b(received|revised|accepted)\[[^\]]*\]\{[^}]*\}/g;
    let match
    while ((match = regex.exec(state.doc.toString())) !== null) {
      decorations.push(Decoration.replace({
        widget: new ReadOnlyWidget(match[0]),
        inclusive: true,
        block: false
      }).range(match.index, match.index + match[0].length))
    }
    return Decoration.set(decorations)
  })
  class ReadOnlyWidget extends WidgetType {
    constructor(content) {
      super()
      this.content = content
    }
    eq(other) { return other.content === this.content }
    toDOM() {
      let span = document.createElement("span")
      span.className = "cm-readonly"
      span.textContent = this.content
      return span
    }
    ignoreEvent() { return true }
  }

  const readOnlyTheme = EditorView.baseTheme({
    '.cm-readonly': {
      background: '#f0f0f0',
      color: '#333',
      cursor: 'not-allowed',
      userSelect: 'none'
    }
  })

  const preventReadOnlyEdit = EditorState.transactionFilter.of(tr => {
    if (!tr.docChanged) return tr
    let changes = []
    tr.changes.iterChanges((fromA, toA, fromB, toB, inserted) => {
      let inReadOnly = false
      tr.startState.field(changeField).between(fromA, toA, (from, to, value) => {
        if (value.spec.class && value.spec.class.includes('cm-readonly')) {
          if (from < toA && to > fromA) inReadOnly = true
        }
      })
      // if (!inReadOnly) {
      //   changes.push({from: fromA, to: toA, insert: inserted})
      // }
    })
    return changes.length ? [tr, { changes }] : tr
  })

  useEffect(() => {
    if (!editorRef.current) return;

    // Enable spell check by default for Master Copier role (only once per session)
    if (userProfile.role === "Master Copier" && !window.spellCheckSettings?._initialized) {
      window.spellCheckSettings = { enabled: true, us: true, uk: false, _initialized: true };
      document.dispatchEvent(new CustomEvent('spellCheckSettingsChanged', {
        detail: window.spellCheckSettings
      }));
    }

    // Conditional extensions based on role
    const readOnlyExtensions = userProfile.role === "Author"
      ? [readOnlyRanges, readOnlyTheme, preventReadOnlyEdit]
      : [];
    const inlpExtensions = userProfile.role === "Copy Editor"
      ? [inlpField]
      : [];

    const startState = EditorState.create({
      doc: fileContent,
      extensions: [
        basicSetup,
        latex,
        html(),
        texLinter,
        placeholders,
        spellCheckExtension,
        spellCheckTheme,
        updateListener,
        mathClickablePlugin,
        EditorState.readOnly.of(readOnly),
        lintGutter({ hoverTime: 0 }),
        annotationsTheme,
        ...readOnlyExtensions,
        ...(altTextFlow ? altTextReadOnlyExtension : []),
        inlpField,
        ...inlpExtensions,
        keymap.of([indentWithTab,
          {
            key: "Alt-g",
            run: () => {
              handleGoToLineOpen();
              return true;
            },
            preventDefault: true
          },
          {
            key: "F7",
            run: (editorView) => {
              // Toggle spell check (US English by default) and sync settings UI
              let enabled;
              if (window.spellCheckSettings) {
                enabled = !window.spellCheckSettings.enabled;
                window.spellCheckSettings.enabled = enabled;
                window.spellCheckSettings.us = true;
                window.spellCheckSettings.uk = false;
              } else {
                enabled = true;
                window.spellCheckSettings = { enabled: true, us: true, uk: false, _initialized: true };
              }
              // Mark that the user has manually toggled spell check
              window.spellCheckSettings._initialized = true;
              window.spellCheckSettings._userToggled = true;
              // Dispatch event so spellChecker.js updates its local settings
              document.dispatchEvent(new CustomEvent('spellCheckSettingsChanged', {
                detail: window.spellCheckSettings
              }));
              // Force the editor to re-evaluate spell check decorations
              editorView.dispatch({
                effects: StateEffect.appendConfig.of([])
              });
              // Update the settings UI checkbox if present
              setTimeout(() => {
                const toggle = document.getElementById('spell-check-toggle');
                if (toggle) toggle.checked = enabled;
              }, 10);
              return true;
            },
            preventDefault: true
          }

        ]),
        undoneChangesField,
        // inlpUndoRedoHandler,
        ...readOnlyExtensions, // Conditionally applied extensions
      ],
    });

    const view = new EditorView({
      state: startState,
      parent: editorRef.current,
      dispatch: async (transaction) => {
        view.update([transaction]);
        const selection = view.state.selection;
        let text = '';
        let line = null;
        let from = null;
        let to = null;
        for (const range of selection.ranges) {
          text += view.state.doc.sliceString(range.from, range.to);
          line = view.state.doc.lineAt(range.from).number;
          from = range.from;
          to = range.to;
        }
        if (line !== "") {
          setShowCommentButton(false);
          setSelectedText({ linenumber: line, selectText: text, fromLine: from, toLine: to });
          setCommentSidebar({ selectValue: { linenumber: line, selectText: text, fromLine: from, toLine: to } });
        }
      }
    });
    setView(view);
    let isApiCallInProgress = false;
    let lastCallTimestamp = 0;
    const DEBOUNCE_TIMEOUT = 300;

    const handleDoubleClick = async (event) => {
      const currentTime = Date.now();
      if (isApiCallInProgress || currentTime - lastCallTimestamp < DEBOUNCE_TIMEOUT) {
        return;
      }

      const pos = view.posAtCoords({ x: event.clientX, y: event.clientY });
      if (pos !== null) {
        isApiCallInProgress = true;
        lastCallTimestamp = currentTime;

        try {
          const line = view.state.doc.lineAt(pos).number;
          const cacheKey = `synctex_${line}_${userProfile.docid}`;
          const cachedResult = sessionStorage.getItem(cacheKey);

          if (cachedResult) {
            setHighlights(JSON.parse(cachedResult));
          } else {
            const data = {
              line: line,
              column: 1,
              docid: userProfile.docid
            }

            const url = `${pathconfig[packjson.environment].baseUrl}${pathconfig[packjson.environment].synctexttopdf}`;
            const response = await axios.post(url, data);
            const pdfdata = response.data?.pdf;

            sessionStorage.setItem(cacheKey, JSON.stringify(pdfdata));
            setHighlights(pdfdata);
          }
        } finally {
          isApiCallInProgress = false;
        }
      }
    };

    editorRef.current.addEventListener('dblclick', handleDoubleClick);

    const handleContextMenu = (event) => {
      event.preventDefault();
      const { clientX, clientY } = event;
      const { left, top } = editorRef.current.getBoundingClientRect();
      const selection = view.state.selection;
      let text = '';
      for (const range of selection.ranges) {
        text += view.state.doc.sliceString(range.from, range.to);
      }
      //const selectedText1 = view.state.getSelection();
      if (text.length !== 0) {
        setContextMenu({
          visible: true,
          x: clientX - (left + 20),
          y: clientY - top,
        });
      };
    }
    const handleClickOutside = () => {
      setContextMenu({
        visible: false,
        x: 0,
        y: 0,
      });
    }

    // Add keyboard event listener for delete.delete.cut operations
    const handleKeyboardCut = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'x') {
        // This will help us identify that the next delete operation is from a delete.cut
        view.dispatch({
          annotations: Transaction.userEvent.of('delete.cut')
        });
      }
    };

    //right click
    editorRef.current.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('click', handleClickOutside);
    editorRef.current.addEventListener('keydown', handleKeyboardCut);

    setStateDoc(startState);
    setLang(startState.facet(latex)?.name);
    setState(startState);
    setView(view);
    if (view) setMainView(view);
    setViewDoc(view);
    setVisual(isVisual(view));

    // Removed initial focus on editor to defer focusing until Joyride is closed/skipped/finished
    // setTimeout(() => {
    //   view.focus(); // Programmatically focus the editor
    //   const clickEvent = new MouseEvent("click", {
    //     bubbles: true,
    //     cancelable: true,
    //     view: window,
    //   });
    //   view.dom.dispatchEvent(clickEvent); // Dispatch the click event
    // }, 100);
    return () => {
      view.destroy();
      editorRef.current?.removeEventListener('keydown', handleKeyboardCut);
    };
  }, [fileContent, userProfile]);

  // Keep a synchronous snapshot of the latest inlpActions so update listeners
  // (which close over potentially stale state) can read the current list.
  useEffect(() => {
    inlpActionsLiveRef.current = Array.isArray(inlpActions) ? inlpActions : [];
  }, [inlpActions]);

  useEffect(() => {
    if (view && inlpActions && inlpActions.length > 0) {
      // Synchronize positions before updating decorations
      const currentDoc = view.state.doc;
      const syncedActions = syncInlpPositions(inlpActions, currentDoc);

      // Update global state with synchronized positions if there were changes
      const hasPositionChanges = syncedActions.some(action => action.positionSynced);
      if (hasPositionChanges) {
        console.log('Position synchronization detected, updating INLP actions');
        setInlpActions(syncedActions);
        return; // Let the next effect cycle handle the decoration update
      }

      // Create a map to track which positions have been processed
      const processedPositions = new Map();

      // Filter out any duplicate actions at the same position

      const uniqueActions = inlpActions.filter((action) => {
        const posKey = `${action.from}-${action.to}`;

        if (processedPositions.has(posKey)) return false;

        processedPositions.set(posKey, true);

        return true;
      });

      const updatedActions = uniqueActions.map((action) => {
        // Get the line text for each action if not already present

        if (!action.fullLine && view?.state?.doc) {
          try {
            const line = view.state.doc.lineAt(action.from);

            return {
              ...action,

              fullLine: line?.text,

              id: action.id || `inlp-${action.from}-${action.to}-${Date.now()}`,
            };
          } catch (e) {
            console.error("Error getting line text:", e);

            return action;
          }
        }

        return {
          ...action,

          id: action.id || `inlp-${action.from}-${action.to}-${Date.now()}`,
        };
      });

      view.dispatch({
        effects: updateInlpDecorations(updatedActions),
      });
    } else if (view && (!inlpActions || inlpActions.length === 0)) {
      // Clear all decorations if there are no actions

      view.dispatch({
        effects: inlpEffect.of([]),
      });
    }
  }, [inlpActions, view]);

  // When the user manually deletes text (without Accept/Ignore/Ignore All) that
  // overlaps one or more INLP suggestions, record those suggestions on the INLP
  // undo stack and remove them from state. This lets Undo (Ctrl+Z) restore the
  // suggestion's action/tooltip/highlight along with the text. (Previously the
  // deletion pushed nothing to the stack, so Undo brought the text back but the
  // decoration stayed lost.)
  const captureManuallyDeletedInlpActions = (deletedRanges) => {
    const ranges = Array.isArray(deletedRanges) ? deletedRanges : [deletedRanges];
    if (!ranges.length) return;
    const currentActions = inlpActionsLiveRef.current || [];
    if (!currentActions.length) return;

    // An action is affected if its range overlaps any deleted range. Zero-width
    // (insert-type) suggestions count when they sit inside a deleted span.
    const overlaps = (action) =>
      ranges.some(({ from, to }) =>
        action.from === action.to
          ? action.from >= from && action.from <= to
          : action.from < to && action.to > from
      );

    const deleted = currentActions.filter(overlaps);

    if (!deleted.length) return;

    // Capture original geometry/content so the suggestion can be rebuilt as-is.
    const capturedActions = deleted.map((action) => ({
      originalText: action.highlightText,
      newText: action.replaceText,
      from: action.from,
      to: action.to,
      fullLine: action.fullLine,
      actionData: { ...action },
    }));

    window.appContext.pushToUndoStack({
      type: 'manual-delete',
      actions: capturedActions,
    });

    // Drop the now-orphaned suggestions from state so they don't linger with
    // broken positions. They are re-added from the undo stack on Undo.
    setInlpActions((prev) =>
      (Array.isArray(prev) ? prev : []).filter(
        (action) =>
          !deleted.some(
            (d) =>
              d.from === action.from &&
              d.to === action.to &&
              d.highlightText === action.highlightText &&
              d.replaceText === action.replaceText
          )
      )
    );

    // Keep the live snapshot consistent for any subsequent change in this same
    // transaction batch.
    inlpActionsLiveRef.current = currentActions.filter(
      (action) =>
        !deleted.some(
          (d) =>
            d.from === action.from &&
            d.to === action.to &&
            d.highlightText === action.highlightText &&
            d.replaceText === action.replaceText
        )
    );
  };

  const adjustInlpActions = (changeFrom, changeTo, lengthChange, isRedo = false) => {
    setInlpActions((currentActions) => {
      // If this is a redo operation and we have inlpRedoStack items,
      // we should be careful not to adjust positions for actions being redone
      if (isRedo && window.appContext && window.appContext.inlpRedoStack.length > 0) {
        const lastRedoAction = window.appContext.inlpRedoStack[window.appContext.inlpRedoStack.length - 1];

        // Don't adjust positions for the action being redone
        return currentActions.map((action) => {
          if (action.from >= changeFrom &&
            !(lastRedoAction.from === action.from && lastRedoAction.to === action.to)) {
            return {
              ...action,
              from: action.from + lengthChange,
              to: action.to + lengthChange,
            };
          }
          return action;
        });
      }

      // Normal adjustment for non-redo operations
      return currentActions.map((action) => {
        if (action.from >= changeFrom) {
          return {
            ...action,
            from: action.from + lengthChange,
            to: action.to + lengthChange,
          };
        }
        return action;
      });
    });
  };

  // Create an update listener

  const updateListener = EditorView.updateListener.of((update) => {
    if (update.docChanged) {
      if (update.transactions.some(tr => tr.annotation(Transaction.userEvent) === 'input.ignore')) {
        // Still update the editor content but don't track changes
        const newContent = update.state.doc.toString();
        setCurrentFile(newContent);
        setEditorContent(newContent);
        return;
      }
      const newContent = update.state.doc.toString();
      const stableDoc = update.state.doc;
      const stableContent = newContent;

      update.transactions.forEach((tr) => {
        const userEvent = tr.annotation(Transaction.userEvent);
        const isRedo = userEvent === 'redo';
        // A manual deletion is any user-initiated delete (selection, backspace,
        // forward-delete, cut). Accept/Ignore/Reject use their own annotations
        // ('input.*') and undo/redo are handled separately, so they are excluded.
        const isManualDelete = typeof userEvent === 'string' && userEvent.startsWith('delete');

        // Pass 1: before any positions shift, capture every INLP suggestion whose
        // text is removed by this manual deletion as a SINGLE undo entry (one
        // Ctrl+Z reverts the whole transaction, so we must not push more than one
        // entry per transaction). This lets Undo restore the suggestion later.
        if (isManualDelete && window.appContext) {
          const deletedRanges = [];
          tr.changes.iterChanges((fromA, toA) => {
            if (toA > fromA) deletedRanges.push({ from: fromA, to: toA });
          });
          if (deletedRanges.length) {
            captureManuallyDeletedInlpActions(deletedRanges);
          }
        }

        // Pass 2: shift positions of the surviving suggestions.
        tr.changes.iterChanges((fromA, toA, fromB, toB, inserted) => {
          const lengthChange = inserted.length - (toA - fromA);
          adjustInlpActions(fromA, toA, lengthChange, isRedo);
        });
      });

      // Only set save trigger if content has actually changed
      if (savedFileContentData !== stableContent) {
        setCurrentFile(stableContent);
        setEditorContent(stableContent);
        setSaveTrigger(true);
      }

      if (window.debouncedAQTimer) {
        clearTimeout(window.debouncedAQTimer);
      }
      if (window.appContext) {
        update.transactions.forEach((tr) => {
          tr.changes.iterChanges((fromA, toA, fromB, toB, inserted) => {
            const lengthChange = inserted.length - (toA - fromA);

            // Adjust positions in undo stack
            if (window.appContext.inlpUndoStack.length > 0) {
              window.appContext.inlpUndoStack = window.appContext.inlpUndoStack.map(action => {
                if (action.from > toA) {
                  return {
                    ...action,
                    from: action.from + lengthChange,
                    to: action.to + lengthChange,
                    actionData: action.actionData ? {
                      ...action.actionData,
                      from: action.actionData.from + lengthChange,
                      to: action.actionData.to + lengthChange
                    } : null
                  };
                }
                return action;
              });
            }

            // Adjust positions in redo stack
            if (window.appContext.inlpRedoStack.length > 0) {
              window.appContext.inlpRedoStack = window.appContext.inlpRedoStack.map(action => {
                if (action.from > toA) {
                  return {
                    ...action,
                    from: action.from + lengthChange,
                    to: action.to + lengthChange,
                    actionData: action.actionData ? {
                      ...action.actionData,
                      from: action.actionData.from + lengthChange,
                      to: action.actionData.to + lengthChange
                    } : null
                  };
                }
                return action;
              });
            }
          });
        });
      }
      window.debouncedAQTimer = setTimeout(() => {
        // const latestAuthorQuery = [...authorQueryRef.current]; 
        const latestAuthorQuery = Array.isArray(authorQueryRef.current) ? authorQueryRef.current : []; // Store the current state value before update
        if (stableDoc.length === update.state.doc.length && stableContent === update.state.doc.toString()) {
          // This will update AQs from both toolbar and editor changes
          AQFinder(stableDoc, latestAuthorQuery); // Pass the state value explicitly
        }
      }, 750);

      return () => {
        if (window.debouncedAQTimer) {
          clearTimeout(window.debouncedAQTimer);
        }
      };
    }
  });

  const handleGoToLineOpen = () => {
    setShowGoToLineModal(true);
    setGoToLineNumber('');
  };

  const handleGoToLineClose = () => {
    setShowGoToLineModal(false);
  };

  const handleGoToLineSubmit = (e) => {
    e.preventDefault();
    const lineNum = parseInt(goToLineNumber, 10);
    if (isNaN(lineNum) || lineNum <= 0 || !view || lineNum > view.state.doc.lines) {
      toastError(`Invalid line number. Please enter a number between 1 and ${view?.state?.doc?.lines || 1}`);
      return;
    }

    // Go to the specified line
    const pos = view.state.doc.line(lineNum).from;
    view.dispatch({
      selection: { anchor: pos },
      effects: EditorView.scrollIntoView(pos, { y: 'center' }),
    });
    view.focus();

    // Close the modal
    handleGoToLineClose();
  };

  useEffect(() => {
    setTimeout(() => {
      let getComment = CommentUpdateFunction(Comment, commentValue?.comment);
      setChangedComment(getComment);
    }, 1000);
  }, [Comment]);


  function isYesNoQuestion(sentence) {
    // Define regex patterns for yes/no questions
    const yesNoPatterns = [/^(is|are|do|does|did|can|could|will|would|should|has|have|am|was|were)\b/i];
    // Check if the sentence matches any of the yes/no patterns
    return yesNoPatterns.some(pattern => pattern.test(sentence));
  }

  // Notice-type queries that only require an acknowledgement ("Reviewed") from the author.
  // Matched against the full AQ content (whitespace-normalized, case-insensitive). Extend as needed.
  const REVIEW_ONLY_NOTICES = [
    "before submitting, proofread your manuscript and make sure each declaration appears once."
  ];
  function isReviewOnlyQuery(content) {
    if (!content) return false;
    const normalized = content.replace(/\s+/g, ' ').trim().toLowerCase();
    return REVIEW_ONLY_NOTICES.includes(normalized);
  }

  // Auto-generated alt-text queries ("The alt-text for the figures ... has been
  // automatically generated to support accessibility ...") only require a
  // "Verified" acknowledgement instead of the usual reply / no-action-required flow.
  const ALT_TEXT_QUERY_PATTERN = /^the alt-text for the figures\b.*\bhas been automatically generated to support accessibility\.\s*please review the short and long alt-text and make any corrections,?\s*if required\.?$/i;
  function isAltTextQuery(content) {
    if (!content) return false;
    const normalized = content.replace(/\s+/g, ' ').trim().toLowerCase();
    return ALT_TEXT_QUERY_PATTERN.test(normalized);
  }

  useEffect(() => {
    authorQueryRef.current = authorQuery || []; // Update ref whenever authorQuery changes
  }, [authorQuery]);


  const AQFinder = async (value) => {
    const doc = value;
    const extractedQueries = [];

    // Get the entire document content as a single string
    let fullDocContent = '';
    for (let i = 1; i <= doc?.lines; i++) {
      fullDocContent += doc?.line(i).text + '\n';
    }

    // Use a regex that can handle multiline content with nested braces
    const queryRegex = /\\AQ(?:\[[^\]]*\])?\{([^{}]*)\}\{([\s\S]*?(?:\{[\s\S]*?\}[\s\S]*?)*?)\}(?:{})?/gis;

    let match;
    while ((match = queryRegex.exec(fullDocContent)) !== null) {
      // Find the line number by counting newlines before the match position
      const textBeforeMatch = fullDocContent.substring(0, match.index);
      const lineNumber = (textBeforeMatch.match(/\n/g) || []).length + 1;

      extractedQueries.push({
        linenumber: lineNumber,
        query: match[2],
        label: match[1],
        wholematch: match[0],
        attachment: [],
        ActivityID: userProfile.ActivityID,
        Role: userProfile.role,
        Date: moment(new Date()).format("DD/MM/YYYY")
      });
    }

    // Merge with existing authorQuery state
    const currentAuthorQuery = Array.isArray(authorQueryRef.current)
      ? authorQueryRef.current
      : [];

    const mergedQueries = extractedQueries.map(newQuery => {
      const existingQuery = currentAuthorQuery.find(q => q.label === newQuery.label);

      if (existingQuery) {
        return {
          ...newQuery,
          replies: existingQuery.replies || [],
          repliesMark: existingQuery.repliesMark || false,
          answerCheck: existingQuery.answerCheck,
          replied: existingQuery.replied || false,
          showReplyArea: existingQuery.showReplyArea || false,
          showReplyButton: existingQuery.showReplyButton,
          attachment: existingQuery.attachment || [],
          aqCheck: isYesNoQuestion(newQuery.query),
          reviewOnly: isReviewOnlyQuery(newQuery.query),
          reviewed: existingQuery.reviewed || false,
          altTextQuery: isAltTextQuery(newQuery.query),
          verified: existingQuery.verified || false
        };
      }

      return {
        ...newQuery,
        aqCheck: isYesNoQuestion(newQuery.query),
        reviewOnly: isReviewOnlyQuery(newQuery.query),
        altTextQuery: isAltTextQuery(newQuery.query)
      };
    });

    setAuthorQuery(mergedQueries); // Update state after processing
  };


  function addComment(view, from, to, comment, cid, commentmeta) {
    if (view) {
      view.dispatch({
        effects: commentEffect.of({ from, to, comment, cid, commentmeta })
      });
      view.focus();
    }
  }

  const highlightCommentedText = useCallback((comments) => {
    // console.log(comments, 'comments');
    if (comments) {
      // if (view && view.state) {
      //   comments.forEach((comment) => {
      const { from, to, cid } = comments;
      // Only add the comment if the cid is different from the previous one processed
      if (cid !== prevCid.current) {
        // Use the selectText directly from the comment
        const commentText = comments?.selectText;
        // Call addComment directly with the comment data
        addComment(view, from, to, commentText, cid, comments);
        // Update the state with the new comment
        setComments((prevComments) => ({
          ...prevComments,
          [cid]: [...(prevComments[cid] || []), comments]
        }));
        // Update the previous cid
        prevCid.current = cid;
      }
      // });
      // }
    }
  }, [view, setComments, prevCid]);

  const removeHighlightedText = useCallback((cid) => {
    if (comments != null && cid.cid != undefined) {
      let { from, to } = cid;
      removeComment(view, from, to);
      setComments((prevComments) => {
        const commentArray = prevComments[cid?.cid] || [];
        return {
          ...prevComments,
          [cid.cid]: commentArray.filter(comment => comment?.from !== from || comment?.to !== to)
        };
      });
    }
  }, [view, comments]);

  function removeComment(view, from, to) {
    view.dispatch({ effects: clearCommentEffect.of({ from, to }) });
    view.focus();
    // Update the track changes data
    saveTrackChanges(view?.state);
  }

  const focusOnLine = (item) => {
    // 1. Check if view exists
    if (!view) return;

    try {
      // 2. Handle case with line number and from/to positions
      if (item.linenumber !== undefined) {
        const pos = view.state.doc.line(item.linenumber).from;
        const from = item.from;
        const to = item.to;

        if (from !== undefined && to !== undefined) {
          // 3. Select text range and center it
          view.dispatch({
            selection: { anchor: from, head: to },
            effects: EditorView.scrollIntoView(from, {
              y: 'center',
              x: 'center',
              yMargin: 80 // Add margin for better visibility
            })
          });

        } else {
          // 4. Just center the line if no from/to
          view.dispatch({
            selection: { anchor: pos },
            effects: EditorView.scrollIntoView(pos, {
              y: 'center',
              x: 'center',
              yMargin: 80
            })
          });
        }
      }
      // 5. Handle case with only from/to positions
      else if (item.from !== undefined && item.to !== undefined) {
        view.dispatch({
          selection: { anchor: item.from, head: item.to },
          effects: EditorView.scrollIntoView(item.from, {
            y: 'center',
            x: 'center',
            yMargin: 80
          })
        });
      }
      // 6. Handle case with just a line number
      else if (typeof item === 'number') {
        const pos = view.state.doc.line(item).from;
        view.dispatch({
          selection: { anchor: pos },
          effects: EditorView.scrollIntoView(pos, {
            y: 'center',
            x: 'center',
            yMargin: 80
          })
        });
      }

      // 7. Focus the editor
      view.focus();

      // 8. Update track changes
      saveTrackChanges(view.state);

    } catch (error) {
      console.error('Error in focusOnLine:', error);
    }
  };

  useEffect(() => {
    // debugger
    if (linepostion?.line) {
      focusOnLineError(linepostion);
    }
  }, [linepostion]);

  const focusOnLineError = (item) => {
    // debugger
    // Get the position of the start of the line
    const pos = view.state.doc.line(item.line === undefined ? item.position : item.line).from;
    view.dispatch({
      effects: EditorView.scrollIntoView(pos, { y: 'center' }),
      selection: { anchor: pos }
    });
    view.focus();
    // Update track changes data for the focused line
    saveTrackChanges(view.state);
  };

  const AuthorQueryDelete = (QueryDelete) => {
    if (!view) return;
    const { linenumber, Content: wholeMatch } = QueryDelete;
    // Adjusting to 1-based indexing for line numbers
    if (linenumber <= 0 || linenumber > view.state.doc.lines) {
      // console.error(`Invalid line number ${linenumber}`);
      return;
    }
    const line = view.state.doc.line(linenumber);
    const lineContent = line.text;
    if (lineContent !== undefined) {
      const updatedLineContent = lineContent.replace(wholeMatch, '');
      const transaction = view.state.update({
        changes: {
          from: line.from,
          to: line.to,
          insert: updatedLineContent
        }
      });
      view.dispatch(transaction);
    }
  };

  const AuthorQueryUpdate = (QueryDetails) => {
    if (!view) return;
    const lineNumber = QueryDetails.linenumber;
    const editedContent = QueryDetails.changecontent;
    const label = QueryDetails.label;

    if (lineNumber <= 0 || lineNumber > view.state.doc.lines) {
      return;
    }

    const line = view.state.doc.line(lineNumber);
    const lineContent = line.text;

    if (lineContent !== undefined) {
      const regex = /\\AQ(?:\[[^\]]*\])?\{([^{}]*)\}\{((?:[^{}]|{(?:[^{}]|{[^{}]*})*})*)\}(?:{})?/;
      const match = QueryDetails.oldcontent.match(regex);

      if (match) {
        const updatedContent = lineContent.replace(
          match[0],
          `\\AQ{${label}}{${editedContent}}`
        );

        const tr = view.state.update({
          changes: {
            from: line.from,
            to: line.to,
            insert: updatedContent
          }
        });
        view.dispatch(tr);
      }
    }
  };


  const commentAction = () => {
    let commentdatas = userProfile.role === "Master Copier" ? "tackandcomment" : "comment";
    setCommentSidebar({ selectValue: selectedText, showSidebar: 'rightbar', commentValue: commentdatas })
  }

  // Handle symbol data received from ToolbarItems
  const handleSymbolData = useCallback((symbol) => {
    if (view) {
      const { state } = view;
      const { selection } = state;
      if (selection.main.from !== null) {
        const cursorPos = selection.main.to;
        const symbolText = symbol; // Assuming symbol is the text you want to insert
        const symbolLength = symbolText.length;
        const newPos = cursorPos + symbolLength;
        view.dispatch({
          changes: {
            from: cursorPos,
            to: cursorPos,
            insert: symbolText,
          },
          selection: {
            anchor: newPos,
            head: newPos,
          },
        });
      }
    }
  }, [view]);

  const handleButton = async (type) => {
    setShowprogress(true)
    const docid = userProfile.docid;
    switch (type) {
      case "styleediting": {
        setProgressDetails(true);
        setProgressDetails("Please wait Style Editing is progress.")
        const data = { 'texfilename': currentFile, 'texcontent': fileContent, "s5filename": " _S2950338824000081-20240617_080312_S5.xml ", "s5content": "UTF-8 base64 encoded cnt", "inifilename": " ELS_user_preedit-mapping.ini", "inicontent": "UTF-8 base64 encoded cnt", "docid": docid };
        const result = await styleEditing(data);
        props.styleEditingError(result);
        setShowprogress(false)
        break;
      }
      case "issueupdation": {
        setProgressDetails(true);
        setProgressDetails("Please wait Issue Updation is progress.")
        const data = { 'texfilename': currentFile, 'texcontent': fileContent, "docid": docid };
        const result = await issueUpdation(data);
        props.issueUpdationError(result);
        setShowprogress(false)
        break;
      }
      case "XMPCreation": {
        setProgressDetails(true);
        setProgressDetails("Please wait XMP Creation is progress.")
        const data = { 'texfilename': currentFile, 'texcontent': fileContent, "docid": docid };
        const result = await XMPCreation(data);
        props.XMPCreationError(result);
        setShowprogress(false)
        break;
      }
      case "referanceordering": {
        setProgressDetails(false);
        setProgressDetails("Please wait Reference Ordering is progress -")
        const runningApi = (val) => {
          setApiStatusRunning(val)
        }
        const refData = { 'texfilename': currentFile, 'texcontent': fileContent };
        const refResult = await referanceOrdering(refData, runningApi);
        props.refrenceStrucutre(refResult);
        setShowprogress(false)
        break;
      }
      case "clearcachedfiles": {
        if (docid !== undefined && docid !== null) {
          setProgressDetails("Please wait clear cache files is progress.");
          try {
            const clearCache = await clearCacheFiles(docid);
            setShowprogress(false);
            console.log(clearCache, 'clearCache')
            if (clearCache && clearCache.isDeleted) {
              toastSuccess(`${clearCache.message}`);
            } else {
              toastError(clearCache?.details || 'Failed to clear cache files');
            }
          } catch (error) {
            setShowprogress(false);
            toastError('Error clearing cache files');
          }
        } else {
          toastError('Docid is missing.');
        }
        break;
      }

      case "cleanup": {
        setProgressDetails(false);
        setProgressDetails("Please wait clean up is progress")
        const cleanData = { 'texfilename': currentFile, 'texcontent': fileContent, "docid": docid };
        const cleanResult = await CleanupEditor(cleanData);
        props.MacroCleanUp(cleanResult);
        setShowprogress(false)
        break;
      }
      case "iref": {
        setProgressDetails(false);
        setProgressDetails("Please wait iref is progress");
        const irefData = { 'texfilename': currentFile, 'texcontent': fileContent, "docid": docid };
        const irefResult = await iRefEditor(irefData);
        props.iRefProgress(irefResult);
        console.log(irefResult, 'irefResult')
        setShowprogress(false);
        break;
      }
      case "crossref": {
        setProgressDetails(false);
        setProgressDetails("Please wait crossref is progress");
        const crossrefData = { 'texfilename': currentFile, 'texcontent': fileContent, "docid": docid };
        const crossrefResult = await crossRef(crossrefData);
        props.crossRefProgress(crossrefResult);
        console.log(crossrefResult, 'crossrefResult')
        setShowprogress(false);
        break;
      }
      default:
        console.log(`Unhandled button type: ${type}`);
        break;
    }
  }
  const handleAQInsert = () => {
    if (view) {
      const { state } = view;
      const { selection } = state;
      const selectedText = state.sliceDoc(selection.main.from, selection.main.to);

      if (selectedText) {
        const nextNumber = getNextAQNumber();
        const aqFormat = `\\AQ{${nextNumber}}{${selectedText}}`;

        view.dispatch({
          changes: {
            from: selection.main.from,
            to: selection.main.to,
            insert: aqFormat
          }
        });
      }
    }

    setContextMenu({
      visible: false,
      x: 0,
      y: 0,
    });
  };

  const getNextAQNumber = () => {
    if (authorQuery && authorQuery.length > 0) {
      const sortedQueries = authorQuery.sort((a, b) => {
        const numA = parseInt(a.label.replace(/\D/g, ''));
        const numB = parseInt(b.label.replace(/\D/g, ''));
        return numB - numA;
      });
      const lastNumber = parseInt(sortedQueries[0].label.replace(/\D/g, ''));
      return `Q${lastNumber + 1}`;
    }
    return 'Q1';
  };

  return (
    <div className="latex-editor">
      <div className={`modal-outer ${showProgress ? 'active' : ''}`}>
        <div className="modal confirmation-popup">
          <div className="modal-header">
            <h1>Please Wait</h1>
          </div>
          <div className="modal-body">
            <p>{progressDetails} {ApiStatusRunning.length !== 0 ? <span>{ApiStatusRunning}</span> : ''} </p>
            <span className="loader"></span>
          </div>
        </div>
      </div>
      <div className='editor-btns-group'>
        <div className='editor-btn-inner'>
          <div style={{ display: "flex" }}>
            <div className='buttonTools-area-size'>
              <div ref={buttonResize} className='buttonTools-area'>
                <ToolbarItems
                  state={state}
                  view={view}
                  overflowed={false}
                  languageName={lang}
                  visual={visual}
                  disabled={showCommentButton}
                  handleSymbolData={handleSymbolData} // Pass function down to ToolbarItems
                />
                {/* <button disabled={showCommentButton} onClick={commentAction} className='div-border' title="Comment"><FaRegComment /></button> */}
              </div>
              {/* {!["Author", "Editor", "Master Copier"].includes(userProfile?.role) && ( */}
              {userProfile?.role === "Technical Editor" && (
                <div className='buttonTool-child'>
                  <button className='div-border' aria-label='More optionS'><FaEllipsisH /></button>
                  <div className='buttonTool-child-inner' ref={subChildButton}>
                    <button className='title-icon-btn' title="Cleanup" onClick={() => handleButton('cleanup')}>Cleanup</button>
                    <button className='title-icon-btn' title="Pre Structuring">Pre Structuring</button>
                    {ActivityName === "L1" ? <><button className='title-icon-btn' title="Style Editing" onClick={() => handleButton('styleediting')}>Style Editing</button>
                      <button className='title-icon-btn' title="Referance Ordering" onClick={() => handleButton('referanceordering')}>Reference Ordering</button></> : ''}
                    <button className='title-icon-btn' title="XML Conversion">XML Conversion</button>
                    <button className='title-icon-btn' title="iAutopage" onClick={() => handleButton('iAutopage')}>iAutopage</button>
                    <button className='title-icon-btn' title="iRef" onClick={() => handleButton('iref')}>iRef</button>
                    <button className='title-icon-btn' title="crossRef" onClick={() => handleButton('crossref')}>CrossRef</button>
                    <button className='title-icon-btn' title="Compiler">Compiler</button>
                    <button className='title-icon-btn' title="iprintQC">iprintQC</button>
                    <button className='title-icon-btn' title="XMP Creation" onClick={() => handleButton('issueupdation')}>Issue Updation</button>
                    <button className='title-icon-btn' title="XMP Creation" onClick={() => handleButton('XMPCreation')}>XMP Creation</button>
                    <button className='title-icon-btn' title="Clear cached files" onClick={() => handleButton('clearcachedfiles')}>Clear cached files</button>
                  </div>
                </div>
              )}
            </div>
            {/* <div className='switch_track div-border'>
          <Form>
            <Form.Check
              type="switch"
              id="track_changes"
              label="Track Changes"
              onChange={(e) => trackToggle(e)}
              disabled="true"
            />
          </Form>
        </div> */}
          </div>
        </div>
        <div className='editor-btn-inner'>
          <Button className="switchbutton" onClick={() => setLayout('pdfonly')}> <FaRegFilePdf /> Switch to pdf</Button>
        </div>
      </div>
      <div ref={editorRef} />
      {contextMenu.visible && (
        <div className='quickbar-toolbar'
          style={{
            top: contextMenu.y,
            left: contextMenu.x,
          }}
        >
          <ToolbarItems
            state={state}
            view={view}
            overflowed={false}
            languageName={lang}
            visual={visual}
            disabled={showCommentButton}
            handleSymbolData={handleSymbolData}
          />
          <button disabled={showCommentButton} onClick={commentAction} className='div-border' title="Comment"><FaRegComment /></button>
          {!["Author", "Editor", "Master Copier"].includes(userProfile.role) && (
            <button onClick={handleAQInsert} className="div-border" title="Insert AQ">
              <FaTag />
            </button>
          )}
        </div>
      )}
      {/* Custom Go to Line Modal */}
      {showGoToLineModal && (
        <div className="go-to-line-modal-overlay" onClick={handleGoToLineClose}>
          <div className="go-to-line-modal" onClick={(e) => e.stopPropagation()}>
            <div className="go-to-line-modal-header">
              <h3 className="go-to-line-modal-title">Go to Line</h3>
              <button className="go-to-line-modal-close" onClick={handleGoToLineClose}>&times;</button>
            </div>
            <div className="go-to-line-modal-body">
              <form onSubmit={handleGoToLineSubmit}>
                <input
                  type="number"
                  id='go-to-line-input'
                  className="go-to-line-modal-input"
                  placeholder={`Line number (1-${view?.state?.doc?.lines || 1})`}
                  value={goToLineNumber}
                  onChange={(e) => {
                    // Only allow numeric input
                    const value = e.target.value;
                    if (value === '' || /^[0-9]+$/.test(value)) {
                      setGoToLineNumber(value);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      // Programmatically click the Go button
                      goButtonRef.current?.click();
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      // Programmatically click the Cancle button
                      cancleButtonRef.current?.click();
                    }
                  }}
                  onKeyPress={(e) => {
                    // Prevent non-numeric characters
                    if (!/[0-9]/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  autoFocus
                  min="1"
                  max={view?.state?.doc?.lines || 1}
                />
              </form>
            </div>
            <div className="go-to-line-modal-footer">
              <button
                ref={cancleButtonRef}
                className="go-to-line-modal-button go-to-line-modal-button-secondary"
                onClick={handleGoToLineClose}
              >
                Cancel
              </button>
              <button
                ref={goButtonRef}
                className="go-to-line-modal-button go-to-line-modal-button-primary"
                onClick={handleGoToLineSubmit}
              >
                Go
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

const CodeMirrorStateContext = createContext < EditorState | undefined > (undefined)

export const useCodeMirrorStateContext = () => {
  const context = useContext(CodeMirrorStateContext)
  if (!context) {
    return null;
  }
  return context
}

/**
 * Tokenize text for word-level diff.
 *
 * Each token is a non-whitespace run plus its immediately following whitespace
 * (e.g. "word " or "word.\n"). Leading whitespace (before the first word) is
 * emitted as its own token so the concatenation of all tokens always reproduces
 * the input string exactly, preserving character offsets.
 *
 * Rationale: the old tokenizer emitted alternating word/space tokens, which
 * let lone whitespace tokens act as LCS anchors. This caused a single logical
 * edit (e.g. "old phrase, keyword" → "new phrase, keyword") to be split into
 * multiple tracked-change segments wherever a space happened to align between
 * old and new text.  Using "word+trailing-space" tokens eliminates lone
 * whitespace tokens, so adjacent delete/insert ops are always coalesced into
 * one replace segment.
 */
const tokenizeForDiff = (text) => {
  const tokens = [];
  let lastEnd = 0;
  const re = /\S+\s*/g;
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > lastEnd) {
      // Leading whitespace before the very first word — keep it as its own token.
      tokens.push(text.slice(lastEnd, m.index));
    }
    tokens.push(m[0]);
    lastEnd = m.index + m[0].length;
  }
  if (lastEnd < text.length) {
    // Trailing whitespace with no following word.
    tokens.push(text.slice(lastEnd));
  }
  return tokens;
};

/**
 * Word-level diff between two strings using LCS over word/whitespace tokens.
 *
 * Returns an ordered array of segments. Each segment is one of:
 *   { op: 'equal',   oldText, newText, oOffset, nOffset }
 *   { op: 'delete',  oldText,          oOffset, nOffset }
 *   { op: 'insert',           newText, oOffset, nOffset }
 *   { op: 'replace', oldText, newText, oOffset, nOffset }
 *
 * `oOffset` is the start offset (in characters) inside oldValue;
 * `nOffset` is the start offset (in characters) inside newValue.
 *
 * Adjacent runs of deletes and inserts are collapsed into a single 'replace'
 * so the UI shows them as one paired edit at the same spot.
 */
const computeWordDiff = (oldValue, newValue) => {
  const A = tokenizeForDiff(oldValue);
  const B = tokenizeForDiff(newValue);
  const n = A.length, m = B.length;

  // Standard LCS length table (bottom-up). For typical alt-text strings the
  // token counts stay well under a few hundred, so an O(n*m) table is fine.
  const dp = [];
  for (let i = 0; i <= n; i++) dp.push(new Uint32Array(m + 1));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] = A[i] === B[j]
        ? dp[i + 1][j + 1] + 1
        : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }

  // Backtrack to produce a flat list of equal / delete / insert ops.
  const ops = [];
  let i = 0, j = 0;
  while (i < n && j < m) {
    if (A[i] === B[j]) { ops.push({ op: 'equal', text: A[i] }); i++; j++; }
    else if (dp[i + 1][j] >= dp[i][j + 1]) { ops.push({ op: 'delete', text: A[i] }); i++; }
    else { ops.push({ op: 'insert', text: B[j] }); j++; }
  }
  while (i < n) ops.push({ op: 'delete', text: A[i++] });
  while (j < m) ops.push({ op: 'insert', text: B[j++] });

  // Coalesce: contiguous equals merge; contiguous delete/insert pairs merge
  // into a single 'replace' (or stay pure delete/insert if the other side
  // is empty). Track running offsets in old- and new-value char space.
  const segments = [];
  let oOffset = 0, nOffset = 0;
  let k = 0;
  while (k < ops.length) {
    if (ops[k].op === 'equal') {
      let text = '';
      while (k < ops.length && ops[k].op === 'equal') { text += ops[k].text; k++; }
      segments.push({ op: 'equal', oldText: text, newText: text, oOffset, nOffset });
      oOffset += text.length;
      nOffset += text.length;
      continue;
    }
    let delText = '', insText = '';
    while (k < ops.length && (ops[k].op === 'delete' || ops[k].op === 'insert')) {
      if (ops[k].op === 'delete') delText += ops[k].text;
      else insText += ops[k].text;
      k++;
    }
    if (delText && insText) {
      segments.push({ op: 'replace', oldText: delText, newText: insText, oOffset, nOffset });
      oOffset += delText.length;
      nOffset += insText.length;
    } else if (delText) {
      segments.push({ op: 'delete', oldText: delText, oOffset, nOffset });
      oOffset += delText.length;
    } else {
      segments.push({ op: 'insert', newText: insText, oOffset, nOffset });
      nOffset += insText.length;
    }
  }
  return segments;
};

/**
 * Apply a tracked-change update for a single alt-text field, rendering each
 * word-level change as its own insertion/deletion mark.
 *
 * Strategy:
 *   1. Compute a word-level diff between oldValue (the current alt-text
 *      content in the doc) and newValue (the user's new text).
 *   2. For each non-equal segment, dispatch a small per-segment document
 *      change that only modifies the span that actually changed.
 *   3. Emit a paired insertion/deletion effect per segment with a unique
 *      per-save sub-key (`lookupKey#<saveId>#<segmentIndex>`).
 *
 * Why per-segment changes (instead of one big replace of [from, to)):
 *   Previously-tracked marks from earlier saves of the SAME alt text live
 *   inside `[from, to)`. A single big replace would drop every decoration
 *   inside that range via the changeField patch logic, wiping the earlier
 *   tracked-change history. Narrow per-segment changes only touch the
 *   spans the user actually edited this save, so unrelated prior marks
 *   are preserved automatically via CodeMirror's standard decoration
 *   mapping.
 *
 * The transaction is annotated with userEvent `input.alttext`, which the
 * altText read-only transactionFilter explicitly whitelists. The same
 * userEvent is also added to the editor's update-listener skip set so it
 * doesn't auto-emit a duplicate whole-range insertion on top of our
 * per-segment effects.
 */
export const updateAltTextWithTracking = ({ from, to, oldValue, newValue, altTextKey, onAfterUpdate }) => {
  if (!globalView) return;
  const state = globalView.state;

  const lookupKey = altTextKey || String(from);

  // 1. Compute the word-level diff.
  const segments = computeWordDiff(oldValue, newValue);
  const hasChanges = segments.some(s => s.op !== 'equal');
  if (!hasChanges) return;

  const baseMeta = {
    Username: userDetails?.username,
    role: userDetails?.role,
    Date_Time: new Date().toLocaleString(),
    cmdtype: 'trackchanges',
    status: '',
  };

  // Unique-per-save suffix so segment keys from a later save don't collide
  // with marks from an earlier save of the same alt text.
  const saveId = Date.now();

  // 2. Build per-segment changes (OLD-doc coords; CodeMirror requires
  //    changes in ascending document order) and matching effects
  //    (positioned in NEW-doc coords, where the inserted text will live
  //    after the transaction applies).
  const changes = [];
  const effects = [];

  segments.forEach((seg, idx) => {
    if (seg.op === 'equal') return;
    const segKey = `${lookupKey}#${saveId}#${idx}`;
    const segMeta = { ...baseMeta, altTextKey: segKey };

    const oFrom = from + seg.oOffset;
    const oldLen = seg.oldText ? seg.oldText.length : 0;
    const oTo = oFrom + oldLen;

    // Document edit for this segment.
    if (seg.op === 'delete') {
      changes.push({ from: oFrom, to: oTo, insert: '' });
    } else if (seg.op === 'insert') {
      changes.push({ from: oFrom, to: oFrom, insert: seg.newText });
    } else if (seg.op === 'replace') {
      changes.push({ from: oFrom, to: oTo, insert: seg.newText });
    }

    // Deletion-widget marker (point widget) for delete/replace segments.
    if (seg.op === 'delete' || seg.op === 'replace') {
      const anchor = from + seg.nOffset;
      effects.push(deletionEffect.of({
        from: anchor,
        to: anchor,
        text: seg.oldText,
        metadata: { ...segMeta },
      }));
    }

    // Insertion mark (range mark) for insert/replace segments.
    if (seg.op === 'insert' || seg.op === 'replace') {
      const iFrom = from + seg.nOffset;
      const iTo = iFrom + seg.newText.length;
      effects.push(insertionEffect.of({
        from: iFrom,
        to: iTo,
        text: seg.newText,
        metadata: { ...segMeta },
      }));
    }
  });

  globalView.dispatch(state.update({
    changes,
    effects,
    selection: { anchor: from + newValue.length },
    scrollIntoView: true,
    annotations: Transaction.userEvent.of('input.alttext'),
  }));

  globalView.focus();
  if (onAfterUpdate) {
    const updatedDoc = globalView.state.doc.toString();
    onAfterUpdate(updatedDoc);
  }
};

const CodeMirrorViewContext = createContext < EditorView | undefined > (undefined)
export const CodeMirrorViewContextProvider = CodeMirrorViewContext.Provider
export const useCodeMirrorViewContext = () => {
  const context = useContext(CodeMirrorViewContext)
  if (!context) {
    return null;
  }
  return context
}


export default CodeMirrorEditor;
